/**
 * LNY Motion — capa de animación del theme sobre GSAP.
 *
 * Los blocks declaran su animación con data-motion="..."; este archivo es el
 * único punto de inicialización. Duraciones, drift y giros salen de los tokens
 * --motion-* de snippets/css-variables.liquid: cambiar el sistema de motion es
 * cambiar tokens, no tocar JS.
 *
 * Handlers disponibles:
 *   parallax    — la imagen deriva dentro de su marco al scrollear (masking)
 *   reveal-text — el texto se revela por líneas al entrar en viewport
 *   lines       — el texto sube línea a línea ligado al scroll (scrub)
 *   reveal      — fade + rise en cascada (cards, features)
 *   spin        — giro suave ligado al scroll (florituras, sellos)
 *   hero-logo   — el wordmark del hero vuela hasta su hueco en la navbar
 *   liquid-divider — la onda que separa modulos: late con el scroll y
 *                 chapotea al pasar el raton (snippets/liquid-divider). Cada
 *                 onda de la pagina viaja en su propio sentido (par/impar por
 *                 orden de aparicion) y en las dos tallas, para que dos ondas
 *                 seguidas no se lean como una secuencia.
 *   moods       — el módulo Moods entero: pegatinas que se pegan, cordón que
 *                 se dibuja, puntero LNY viajero con paradas y dividers con
 *                 física de líquido reactiva al ratón
 *   bcorp       — el estandarte cae plegado y se despliega con su peso; luego
 *                 los compromisos y sus marcas de rotulador
 *   reveal-cta  — como reveal, pero con retardo: el CTA cierra la secuencia
 *
 * Todo vive bajo prefers-reduced-motion: no-preference. Sin JS o con motion
 * reducido, la página es 100% funcional y estática.
 */
/* Bloques de título (title-section): sin GSAP o con motion reducido se ven
   siempre, y la red de seguridad los enseña a los 3 s pase lo que pase. */
(() => {
  const show = () => document.documentElement.classList.remove('js-intro');
  if (!window.gsap || !window.SplitText || matchMedia('(prefers-reduced-motion: reduce)').matches) show();
  else setTimeout(show, 3000);
})();

(() => {
  if (!window.gsap || !window.ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger, SplitText, ...(window.MorphSVGPlugin ? [MorphSVGPlugin] : []));

  const rootStyles = getComputedStyle(document.documentElement);
  const token = (name, fallback) => {
    const value = parseFloat(rootStyles.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  const DURATION = token('--motion-duration-600', 600) / 1000;
  const DRIFT = token('--motion-parallax-drift', 6);
  const SPIN = token('--motion-spin-max', 6);
  const DISTANCE = token('--motion-reveal-distance', 24);
  const LAG = token('--motion-scrub-lag', 0.8);
  /* ⭐ ENTRADAS EN MÓVIL: SE ADELANTAN (13/10). En escritorio una entrada
     arranca cuando su bloque ya asoma (`top 70%`, `top 85%`…): se ve llegar.
     En móvil ese mismo criterio dejaba PANTALLAS VACÍAS — con el dedo se pasa
     una pantalla entera en menos de lo que dura una entrada, y lo que se veía
     era el fondo del módulo esperando a su texto (hallazgo de la revisión web:
     «pantallas en blanco entre secciones»). Aquí la entrada arranca ANTES de
     que el bloque llegue a la pantalla, con la ventaja que diga el token (en %
     del alto de pantalla): al pasar rápido el contenido ya está, y despacio se
     le ve terminar de posarse.
     Solo las entradas (lo que nace oculto y aparece una vez). Lo ligado al
     scroll con `scrub` y los pins no pasan por aquí: no esconden nada. */
  const ENTER_LEAD = matchMedia('(max-width: 749px)').matches ? token('--motion-enter-lead-mobile', 50) : 0;
  const enterAt = (start) => (ENTER_LEAD ? `top ${100 + ENTER_LEAD}%` : start);
  const stringToken = (name, fallback) => rootStyles.getPropertyValue(name).trim() || fallback;
  const EASE = stringToken('--motion-ease-reveal', 'expo.out');
  const FLIGHT_EASE = stringToken('--motion-ease-flight', 'power2.inOut');

  /* Va FUERA del matchMedia: sin motion también hay algo que hacer (el
     póster y, si el editor los activa, los controles). Y va el primero:
     es un pin, y lo que va debajo se crea después. */
  /* MÓDULO 1 · hero-video-scroll
     Carga diferida del vídeo, controles opcionales (ajustes del editor), aviso
     de vídeo externo en el editor y el recorrido con pin: encoger al
     reproductor → pausa → crecer a fondo → la Historia 1 lo tapa (doble
     parallax). Todo ligado al scroll NATURAL: sin autoscroll ni avance
     automático (decisión de Guille, 29/09 noche: el avance al acabar el vídeo
     pegaba un salto). */
  (() => {
    const reduce = matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.querySelectorAll('[data-motion="hero-video-scroll"]').forEach((hero) => {
      const media = hero.querySelector('.hvs__media');
      const cap = hero.querySelector('.hvs__cap');
      const badge = hero.querySelector('.hvs__badge') || {};
      const ui = hero.querySelector('.hvs__ui');
      const video = hero.querySelector('video');
      /* Ajustes del editor (data-* de sections/hero-video) */
      const withSound = hero.dataset.sound === 'true';
      const withControls = hero.dataset.controls === 'true';
      const label = (k) => hero.dataset[k] || '';
      /* Controles: OCULTOS por defecto. Solo si el editor los activa (y hay vídeo). */
      if (!video || !withControls) ui?.remove();

      /* Vídeo externo: el aviso lo pinta Liquid solo en el editor (request.design_mode). */
      const playBtn = hero.querySelector('[data-hvs="play"]');
      const muteBtn = hero.querySelector('[data-hvs="mute"]');
      const fullBtn = hero.querySelector('[data-hvs="full"]');
      const time = hero.querySelector('.hvs__time');
      const bar = hero.querySelector('.hvs__progress span');
      const css = getComputedStyle(hero);
      const px = (name) => parseFloat(css.getPropertyValue(name));

      let syncPlay = () => {}, syncMute = () => {};
      if (video && withControls) {
        /* --- controles del reproductor --- */
        const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
        syncPlay = () => {
          const playing = !video.paused;
          playBtn.setAttribute('aria-label', playing ? label('labelPause') : label('labelPlay'));
          playBtn.querySelector('[data-icon="play"]').hidden = playing;
          playBtn.querySelector('[data-icon="pause"]').hidden = !playing;
        };
        syncMute = () => {
          muteBtn.setAttribute('aria-label', video.muted ? label('labelSoundOn') : label('labelMute'));
          muteBtn.querySelector('[data-icon="on"]').hidden = video.muted;
          muteBtn.querySelector('[data-icon="off"]').hidden = !video.muted;
        };
        video.addEventListener('play', syncPlay);
        video.addEventListener('pause', syncPlay);
        video.addEventListener('volumechange', syncMute);
        video.addEventListener('timeupdate', () => {
          if (!video.duration) return;
          time.textContent = `${fmt(video.currentTime)} / ${fmt(video.duration)}`;
          bar.style.width = `${(video.currentTime / video.duration) * 100}%`;
        });
        /* Si aún no se ha cargado (carga diferida), el primer play lo carga */
        playBtn.addEventListener('click', () => (!started ? startVideo() : video.paused ? video.play() : video.pause()));
        muteBtn.addEventListener('click', () => { video.muted = !video.muted; });
        fullBtn.addEventListener('click', () => (video.requestFullscreen || video.webkitEnterFullscreen)?.call(video));
      }

      /* ---------- Carga diferida y reproducción ----------
         El vídeo no se pide hasta que hace falta (startVideo): a mitad del
         encogido, camino del reproductor. Arranca desde el principio, va en
         BUCLE —nunca se congela en el último fotograma, así que crece sin
         cortes— y sigue solo mientras el hero se vea; fuera de pantalla se
         pausa. Sin sonido salvo que el editor lo active, y aun así, si el
         navegador bloquea el autoplay con sonido, se reproduce en silencio. */
      let started = false;
      const startVideo = () => {
        if (!video) return;
        if (!started) {
          started = true;
          video.src = video.dataset.src;
          video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
        }
        video.loop = true;
        video.muted = !withSound;
        video.currentTime = 0;
        video.play().catch(() => { video.muted = true; video.play(); });
      };
      if (video) {
        new IntersectionObserver(([entry]) => {
          if (!started) return;
          if (entry.isIntersecting) video.play().catch(() => {});
          else video.pause();
        }).observe(hero);
      }

      if (reduce) {
        /* Sin motion: ni encogido ni autoscroll. Se queda el póster; con
           controles activos, el usuario puede darle al play. */
        if (video && withControls) {
          hero.classList.add('is-player');
          syncPlay(); syncMute();
        }
        return;
      }
      if (video && withControls) { syncPlay(); syncMute(); }

      if (!window.gsap || !window.ScrollTrigger) return;

      /* Medidas VIVAS, leídas del CSS en cada cálculo: cada tamaño de pantalla
         define las suyas (en móvil el hero mide la pantalla y el reproductor
         va en vertical). invalidateOnRefresh las recalcula al girar o redimensionar. */
      const tok = (name) => parseFloat(getComputedStyle(hero).getPropertyValue(name));
      const H = () => hero.offsetHeight;
      /* ⚠️ En el theme la cabecera SE ESCONDE al bajar (sección header): el hero
         se fija arriba del todo y el reproductor se centra en la pantalla entera.
         Midiendo la cabecera quedaba una franja vacía de su alto mientras el hero
         estaba fijo. Si se sube, la cabecera reaparece encima, como en todo el sitio. */
      const headerH = () => 0;
      const vw = () => document.documentElement.clientWidth;
      const RADIUS = () => tok('--hvs-player-radius');

      /* Reproductor (16:9 en escritorio, 4:5 en móvil), CENTRADO en la parte
         visible de la pantalla */
      const endW = () => Math.min(tok('--hvs-player-w'), vw() - 2 * tok('--hvs-player-margin'));
      const endH = () => endW() / tok('--hvs-player-ratio');
      const TOP = () => Math.max(tok('--hvs-player-margin'), (Math.min(H(), innerHeight - headerH()) - endH()) / 2);
      const endScale = () => Math.max(endW() / vw(), endH() / H());
      const insetX = () => (vw() - endW() / endScale()) / 2;
      const insetY = () => (H() - endH() / endScale()) / 2;
      const endY = () => TOP() + endH() / 2 - H() / 2;
      const placeUI = () => {
        if (!ui) return;
        ui.style.width = `${endW()}px`;
        ui.style.height = `${endH()}px`;
        ui.style.top = `${TOP()}px`;
      };
      if (video && ui && withControls) { placeUI(); ScrollTrigger.addEventListener('refreshInit', placeUI); }

      /* ---------- YouTube: dos pasos, dos reproductores ----------
         Paso 1 (reposo): de fondo, en bucle y SIN sonido, sin controles. Se
         pide con la página ya cargada, para no competir con el póster (LCP).
         Paso 2 (el reproductor): el de YouTube con sus controles, DESDE EL
         PRINCIPIO y con sonido si el editor lo activa. Va en su propia caja,
         fuera del marco, para que los controles no hereden su escala.
         ⚠️ El sonido solo arranca tras un gesto del usuario (clic, toque o
         tecla; el scroll no cuenta). Se intenta; si a los 0,8 s no está
         reproduciendo, el navegador lo ha bloqueado: sigue en silencio y sale
         el botón «Activar sonido». Con el hero tapado por el módulo
         siguiente, se pausa: no suena lo que no se ve. */
      const ytId = hero.dataset.youtube;
      const ytBox = hero.querySelector('.hvs__yt');
      const ytBg = hero.querySelector('.hvs__yt-bg');
      const ytSound = hero.querySelector('[data-hvs-yt="sound"]');
      let setZone = () => {};
      if (ytId && ytBox) {
        const vars = { playsinline: 1, rel: 0, iv_load_policy: 3 };
        const HOST = 'https://www.youtube-nocookie.com';
        const playing = (p) => [1, 3].includes(p.getPlayerState());
        let bg, fg, fgReady = false, fgPending = null, zone = 'rest', pausedByUs = false, poll;
        const placeYT = () => {
          ytBox.style.width = `${endW()}px`;
          ytBox.style.top = `${TOP()}px`;
        };
        placeYT();
        ScrollTrigger.addEventListener('refreshInit', placeYT);

        const hideSound = () => { ytSound.hidden = true; clearInterval(poll); };
        const askSound = () => {
          ytSound.hidden = false;
          clearInterval(poll);
          /* Si lo activa desde los controles de YouTube, el botón sobra */
          poll = setInterval(() => { if (!fg.isMuted()) hideSound(); }, 500);
        };
        const playFg = (restart) => {
          if (!fgReady) { fgPending = restart; return; }
          if (restart) {
            fg.seekTo(0, true);
            if (withSound) fg.unMute(); else fg.mute();
          }
          fg.playVideo();
          if (restart && withSound) {
            setTimeout(() => {
              if (zone !== 'player' || playing(fg)) return;
              fg.mute(); fg.playVideo(); askSound();
            }, 800);
          }
        };
        ytSound.addEventListener('click', () => {
          hideSound();
          fg.unMute(); fg.seekTo(0, true); fg.playVideo();
          /* Safari no pasa el gesto al iframe: si no arranca, sigue en
             silencio y el sonido queda en los controles de YouTube */
          setTimeout(() => { if (zone === 'player' && !playing(fg)) { fg.mute(); fg.playVideo(); } }, 800);
        });

        const createFg = (YT) => {
          fg = new YT.Player(ytBox.querySelector('[data-hvs-yt="player"]'), {
            host: HOST, videoId: ytId,
            playerVars: { ...vars, controls: 1 },
            events: {
              onReady: () => {
                fg.getIframe().title = label('videoLabel');
                fgReady = true;
                if (fgPending !== null && zone === 'player') playFg(fgPending);
                fgPending = null;
              },
            },
          });
        };
        const createBg = (YT) => {
          bg = new YT.Player(ytBg.querySelector('[data-hvs-yt="bg"]'), {
            host: HOST, videoId: ytId,
            playerVars: { ...vars, autoplay: 1, mute: 1, loop: 1, playlist: ytId, controls: 0, disablekb: 1, fs: 0 },
            events: {
              onReady: () => {
                bg.getIframe().tabIndex = -1;
                bg.mute();
                if (zone === 'rest') bg.playVideo();
              },
              onStateChange: (e) => {
                if (e.data === 1) ytBg.classList.add('is-playing');
                if (e.data === 0) { bg.seekTo(0); bg.playVideo(); }
              },
            },
          });
        };
        const loadYT = () => (window._lnyYT ||= new Promise((resolve) => {
          if (window.YT?.Player) { resolve(window.YT); return; }
          const prev = window.onYouTubeIframeAPIReady;
          window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          document.head.append(tag);
        }));
        const boot = () => loadYT().then((YT) => { ytBox.hidden = false; createBg(YT); createFg(YT); });
        if (document.readyState === 'complete') boot();
        else addEventListener('load', boot, { once: true });

        /* rest = en reposo · player = el reproductor a la vista · covered =
           tapado por el módulo siguiente */
        setZone = (next) => {
          if (next === zone) return;
          const prev = zone;
          zone = next;
          ytBox.classList.toggle('is-on', zone !== 'rest');
          if (zone === 'player') {
            bg?.pauseVideo?.();
            if (prev === 'rest') playFg(true);
            else if (pausedByUs) playFg(false);
            pausedByUs = false;
          } else {
            pausedByUs = zone === 'covered' && fgReady && playing(fg);
            if (fgReady) fg.pauseVideo();
            fgPending = null;
            if (zone === 'rest') { hideSound(); bg?.playVideo?.(); }
          }
        };
      }

      /* ---------- Cúpula que cambia de curvatura ----------
         El borde de abajo es la elipse del Figma (2087 de ancho, fondo a 498),
         pero escrita como perfil: y(x) = lados + bulge · f(x), con f = 1 en el
         centro y 0 en los bordes. bulge > 0 cae sobre el vídeo; bulge < 0 se
         curva hacia arriba. Con la misma forma en los dos sentidos, el cambio
         pasa por plano sin saltos. Escala con el ancho, como la elipse CSS. */
      const capSvg = cap?.querySelector('.hvs__cap-shape');
      const capPath = capSvg?.querySelector('path');
      const capShape = { bend: 1 };   /* 1 = reposo (cae) · -1 = curvada hacia arriba */
      const drawCap = () => {
        if (!capPath) return;
        const w = cap.offsetWidth, h = cap.offsetHeight, k = w / 1440;
        const a = 1043.5 * k, b = 396 * k;          /* semiejes de la elipse */
        const ell = (dx) => Math.sqrt(Math.max(0, 1 - (dx / a) ** 2));
        const side = ell(w / 2);
        const depth = b * (1 - side);                /* lo que cae el centro sobre los lados */
        const sides = h - depth;
        const N = 48;
        let d = `M0,0 L${w},0`;
        for (let i = N; i >= 0; i--) {
          const x = (w / N) * i;
          const f = (ell(x - w / 2) - side) / (1 - side);
          d += ` L${x.toFixed(1)},${(sides + capShape.bend * depth * f).toFixed(1)}`;
        }
        capSvg.setAttribute('viewBox', `0 0 ${w} ${h}`);
        capPath.setAttribute('d', `${d} Z`);
      };
      if (capSvg) {
        drawCap();
        capSvg.removeAttribute('hidden');   /* en <svg>, .hidden = false no quita el atributo */
        cap.classList.add('has-shape');
        ScrollTrigger.addEventListener('refresh', drawCap);
      }

      /* ---------- Recorrido, en px de scroll ----------
         ENCOGER (0-600): la cúpula sube cambiando de curvatura y el vídeo se
         encoge al reproductor (algo más grande que el del Figma).
         PAUSA  (600-900): el reproductor, quieto en el centro.
         TAPAR  (900-1708): el hero sigue fijo y la historia sube por encima con
         su onda y lo tapa (su margin-top negativo = alto del hero). El
         reproductor también sube, pero más despacio: doble parallax, cada capa
         a su velocidad. Ya no vuelve a ampliarse a sangre (Guille, 29/09). */
      const SHRINK = 600, HOLD = 300, COVER = H();
      const COVER_SPEED = token('--motion-cover-speed', 0.4);   /* el vídeo sube más despacio que la historia que lo tapa */
      const TOTAL = SHRINK + HOLD + COVER;
      const P1 = SHRINK;   /* parada: el reproductor */

      /* Valores en función: se recalculan en cada refresh (invalidateOnRefresh).
         ⚠️ El recorte NO se anima como cadena de clip-path: el navegador
         simplifica «inset(0 2px 0 2px round 77px)» a «inset(0 2px round 77px)»
         y GSAP emparejaba mal los números al volver a fondo —el radio acababa
         en el recorte de abajo: las esquinas se perdían y abajo se cortaba una
         franja que luego saltaba—. El CSS compone el clip-path con tres
         variables y aquí solo se animan números (mismo arreglo que en Talento). */
      /* Móvil (--hvs-player: 0, Guille 30/09): SIN reproductor. La cúpula sube
         y deja el vídeo a pantalla completa en vertical; ahí se queda hasta
         que la historia lo tapa. Mismo recorrido, el vídeo no se encoge. */
      const hasPlayer = () => tok('--hvs-player') !== 0;
      const player = () => (!hasPlayer() ? { scale: 1, y: 0, '--hvs-clip-x': '0px', '--hvs-clip-y': '0px', '--hvs-clip-r': '0px' } : {
        scale: () => endScale(), y: () => endY(),
        /* ⚠️ Redondeado y nunca negativo: un «-1.1e-13px» (notación
           científica) no es CSS válido y anula el clip-path entero. */
        '--hvs-clip-x': () => `${Math.max(0, insetX()).toFixed(2)}px`,
        '--hvs-clip-y': () => `${Math.max(0, insetY()).toFixed(2)}px`,
        '--hvs-clip-r': () => `${(RADIUS() / endScale()).toFixed(2)}px`,
      });
      const full = { scale: 1, y: 0, '--hvs-clip-x': '0px', '--hvs-clip-y': '0px', '--hvs-clip-r': '0px' };

      /* Se marca como fijado: solo así la sección
         siguiente sube su alto para taparlo (CSS de sections/hero-video). */
      hero.classList.add('is-pinned');
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: hero,
          start: () => `top ${headerH()}px`,
          end: `+=${TOTAL}`,
          pin: true,
          scrub: LAG,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const y = self.progress * TOTAL;
            hero.classList.toggle('is-player', hasPlayer() && y > P1 - 40);
            if (!started && y > SHRINK * 0.5) startVideo();
            setZone(y <= P1 - 40 ? 'rest' : y < SHRINK + HOLD + COVER * 0.6 ? 'player' : 'covered');
          },
          onLeave: () => setZone('covered'),
        },
      });
      tl.to(cap, { yPercent: -100, duration: SHRINK * 0.55 }, 0)
        .to(capShape, { bend: -1, duration: SHRINK * 0.55, ease: 'power1.inOut', onUpdate: drawCap }, 0)
        .fromTo(media, full, { ...player(), duration: SHRINK, ease: 'power1.inOut', immediateRender: false }, 0)
        /* El sello (B Corp) acompaña al marco: de la parte visible bajo la
           cúpula al centro del reproductor */
        .to(badge, { top: '50%', duration: SHRINK, ease: 'power1.inOut' }, 0)
        /* TAPAR con doble parallax: mientras la historia sube por encima a
           velocidad 1, el reproductor también sube, pero a 0,4. Al ir más
           lento, la historia siempre lo alcanza: el hueco de detrás nunca asoma. */
        .to(media, { y: () => (hasPlayer() ? endY() : 0) - COVER * COVER_SPEED, duration: COVER, ease: 'none' }, SHRINK + HOLD);
      /* El reproductor de YouTube sube con el marco, a su misma velocidad */
      if (ytId && ytBox) tl.to(ytBox, { y: -COVER * COVER_SPEED, duration: COVER, ease: 'none' }, SHRINK + HOLD);
      /* Parada para la guía de scroll: el marco ya encogido, a mitad de la
         pausa. Sin ella, un gesto saltaba de arriba al módulo siguiente y el
         encogido, la pausa y el tapado pasaban de golpe (Guille, 01/10). */
      /* …pero SOLO con vídeo: el marco centrado sirve para verlo y darle al
         play. Con una foto (photo_only, sin <video>) esa parada no aporta
         nada y el gesto lleva directo al módulo siguiente (Guille, 01/10). */
      hero._guideStops = () => (video || ytId ? [tl.scrollTrigger.start + P1 + HOLD / 2] : []);
    });
  })();

  /* MÓDULO · hero-home (cabecera de la home, Figma 40004207:9088 y su
     recorrido 40004305:14527). SIN pin (Guille, 30/09): la página scrollea
     normal y el efecto sale de las velocidades.
     · El marco (margen 64 y esquinas 48, proporción 1440 × 808) se abre a
       sangre en los primeros px de scroll.
     · La foto sube MÁS DESPACIO que la página (va más alta que el hero y se
       desplaza hacia abajo dentro de él): el módulo siguiente, que sube a
       velocidad 1 con su onda, la va tapando.
     · Claim, texto de abajo y pegatinas se van cada uno a su ritmo.
     · El wordmark NO va ligado al scroll: al pasar el umbral sale un vuelo
       con tiempo propio, POR ENCIMA de la navbar (capa fija), y aterriza
       exactamente sobre el logo de la navbar, que aparece en su sitio en el
       mismo fotograma. Así no hay estados a medias (el logo pasando por
       detrás de la barra). Al volver arriba, el vuelo inverso.
     Sin motion o sin GSAP: la foto enmarcada, quieta. */
  (() => {
    const hero = document.querySelector('[data-motion="hero-home"]');
    if (!hero || !window.gsap || !window.ScrollTrigger) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const media = hero.querySelector('.hero-home__media');
    const mark = hero.querySelector('[data-hero-mark]');
    const claim = hero.querySelector('[data-hero-claim]');
    const foot = hero.querySelector('[data-hero-foot]');
    const labelStart = hero.querySelector('.hero-home__label--start');
    const labelEnd = hero.querySelector('.hero-home__label--end');
    const slot = document.querySelector('.header__logo');
    const slotMark = slot?.querySelector('img') || slot;
    const root = document.documentElement;
    const wraps = [...document.querySelectorAll('.shopify-section-group-header-group')];
    /* Arriba del todo la cinta y la navbar ocupan su sitio una encima de otra:
       el hero empieza debajo de las dos y su alto se descuenta de la pantalla */
    const setTop = () => root.style.setProperty('--hh-top', `${wraps.reduce((h, w) => h + w.offsetHeight, 0)}px`);
    setTop();
    ScrollTrigger.addEventListener('refreshInit', setTop);
    /* Una longitud del CSS en px de verdad: las variables vienen en rem
       (--spacing-64 = 4rem) y parseFloat daba 4. Se mide con una sonda. */
    const px = (name) => {
      const probe = document.createElement('div');
      probe.style.cssText = `position:absolute;visibility:hidden;width:var(${name})`;
      hero.append(probe);
      const value = probe.getBoundingClientRect().width;
      probe.remove();
      return value;
    };

    /* ---------- El marco se abre ---------- */
    gsap.fromTo(hero,
      { '--hh-clip': () => `${px('--hh-pad')}px`, '--hh-radius': () => `${px('--hh-radius-rest')}px` },
      {
        '--hh-clip': '0px', '--hh-radius': '0px', ease: 'power2.inOut',
        scrollTrigger: { start: 0, end: () => Math.max(160, px('--hh-pad') * 4), scrub: LAG, invalidateOnRefresh: true },
      });

    /* ---------- Parallax: la foto a menos velocidad que la página ---------- */
    gsap.fromTo(media, { yPercent: 0 }, {
      yPercent: () => token('--motion-cover-speed', 0.4) * 100 * 0.6,
      ease: 'none',
      scrollTrigger: { trigger: hero, start: 0, end: 'bottom top', scrub: true, invalidateOnRefresh: true },
    });

    /* ---------- Cada pieza a su velocidad ---------- */
    /* ⚠️ scrub: true, SIN retraso: con el retraso común (LAG), al bajar
       deprisa las pegatinas se quedaban atrás —parecía que bajaban con la
       página y pisaban el módulo de abajo— y al parar se ponían al día
       subiendo (Guille, 30/09). En un parallax el retraso se lee como rebote. */
    const drift = (el, vars, span = 0.6) => el && gsap.to(el, {
      ...vars, ease: 'none',
      scrollTrigger: { start: 0, end: () => hero.offsetHeight * span, scrub: true, invalidateOnRefresh: true },
    });
    drift(claim, { y: -40, autoAlpha: 0 }, 0.25);
    drift(foot, { y: -120, autoAlpha: 0 }, 0.45);
    drift(labelStart, { y: -220, x: -30 }, 0.9);
    drift(labelEnd, { y: -380, x: 20 }, 1);

    /* ---------- Vuelo del wordmark ---------- */
    if (!mark || !slot) return;
    /* La copia que vuela: en una capa fija por encima de la cabecera */
    const flyer = mark.cloneNode(true);
    flyer.removeAttribute('data-hero-mark');
    flyer.classList.add('hero-home__flyer');
    flyer.setAttribute('aria-hidden', 'true');
    flyer.querySelectorAll('img').forEach((img) => { img.alt = ''; });
    document.body.append(flyer);
    const fLight = flyer.querySelector('.hero-home__mark-light');
    const fInk = flyer.querySelector('.hero-home__mark-ink');
    gsap.set(flyer, { autoAlpha: 0 });
    gsap.set(slot, { autoAlpha: 0 });

    const box = (el) => el.getBoundingClientRect();
    const DURATION = 0.95;
    const ease = gsap.parseEase('power3.inOut');
    let state = 'home';   /* home | flying-up | landed | flying-down */
    let flight = null;
    const hold = (on) => root.classList.toggle('is-hero-hold', on);

    /* ⚠️ Origen y destino se miden EN CADA FOTOGRAMA, no al arrancar: mientras
       vuela, la página sigue bajando (el hueco del hero sube con ella) y la
       navbar sube 45 px al irse la cinta de arriba. Medidos una sola vez, el
       vuelo salía de donde el logo ya no estaba y llegaba a donde la navbar ya
       no estaba: un saltito al arrancar y otro al posarse (Guille, 30/09).
       Interpolando entre las dos cajas VIVAS, en p = 0 está justo sobre el
       logo del hero y en p = 1 justo sobre el de la navbar. */
    const fly = (fromEl, toEl, dir, done) => {
      flight?.kill();
      const k = { p: 0 };
      const render = () => {
        const e = ease(k.p);
        const f = box(fromEl), t = box(toEl);
        gsap.set(flyer, {
          left: f.left + (t.left - f.left) * e,
          top: f.top + (t.top - f.top) * e,
          width: f.width + (t.width - f.width) * e,
        });
        /* Blanco sobre la foto → granate sobre la navbar (o al revés) */
        const ink = gsap.utils.clamp(0, 1, (k.p - 0.35) / 0.4);
        const toInk = dir === 'up' ? ink : 1 - ink;
        fLight.style.opacity = String(1 - toInk);
        fInk.style.opacity = String(toInk);
      };
      render();
      gsap.set(flyer, { autoAlpha: 1, x: 0, y: 0, scale: 1 });
      hold(true);
      flight = gsap.to(k, { p: 1, duration: DURATION, ease: 'none', onUpdate: render, onComplete: () => { done(); hold(false); } });
    };
    const up = () => {
      if (state === 'landed' || state === 'flying-up') return;
      gsap.set(mark, { autoAlpha: 0 });
      state = 'flying-up';
      fly(mark, slotMark, 'up', () => {
        /* Aterriza: el logo de la navbar aparece justo donde está la copia */
        gsap.set(slot, { autoAlpha: 1 });
        gsap.set(flyer, { autoAlpha: 0 });
        state = 'landed';
      });
    };
    const down = () => {
      if (state === 'home' || state === 'flying-down') return;
      gsap.set(slot, { autoAlpha: 0 });
      state = 'flying-down';
      /* Al hueco del hero (oculto, pero con su caja) */
      fly(slotMark, mark, 'down', () => {
        gsap.set(mark, { autoAlpha: 1 });
        gsap.set(flyer, { autoAlpha: 0 });
        state = 'home';
      });
    };
    ScrollTrigger.create({
      /* En px de scroll desde arriba: el vuelo sale al primer gesto */
      start: () => token('--motion-hero-logo-threshold', 24),
      onEnter: up,
      onLeaveBack: down,
    });
    /* Si la página se abre ya bajada (recarga a media página), sin vuelo */
    if (window.scrollY > 24) {
      gsap.set(mark, { autoAlpha: 0 });
      gsap.set(slot, { autoAlpha: 1 });
      state = 'landed';
    }
  })();

  /* MÓDULO · story-slider con «Avanzar con el scroll» (data-story-scroll)
     El módulo se queda fijo y cada tramo de scroll pasa a la siguiente foto
     (con su texto). NO se bloquea el scroll: el paso sale de la posición, no
     de un gesto, así que quien baja deprisa pasa de largo sin quedarse
     atrapado. Flechas y puntos mueven el scroll al punto de esa foto (el
     slider cambia porque cambia el scroll) y «Saltar» lleva al final del
     tramo, al módulo siguiente. Solo en escritorio: en móvil el texto y la
     foto apilados no caben en una pantalla y fijarlo atraparía; allí queda
     el slider normal con flechas. Sin motion: slider normal. */
  gsap.matchMedia().add('(min-width: 750px) and (prefers-reduced-motion: no-preference)', () => {
    const glide = (y, onComplete) => {
      const o = { y: window.scrollY };
      /* Tween de GSAP y no scrollTo smooth: arranca con velocidad desde el
         primer fotograma (caso 6 de la skill conversion-liquid) */
      gsap.to(o, { y, duration: 0.9, ease: 'power2.inOut', onUpdate: () => window.scrollTo(0, o.y), onComplete });
    };
    const made = [];
    document.querySelectorAll('[data-story-scroll]').forEach((root) => {
      const n = root.querySelectorAll('.story-slider__slide, .story-slider__photo').length;
      if (n < 2) return;
      const STEP = () => window.innerHeight * token('--motion-story-step', 0.55);
      let shown = 0;
      /* Mientras flechas o puntos llevan el scroll a una foto, el recorrido
         pasa por las de en medio: no se enseñan, se va directo a la pedida */
      let jumping = null;
      const st = ScrollTrigger.create({
        trigger: root,
        /* Fijo con su onda de entrada JUSTO por encima de la pantalla: así,
           mientras está fijo, se ve su fondo entero y no la franja de lo que
           había detrás de la onda */
        start: () => `top+=${root.querySelector('.story-slider__wave')?.offsetHeight || 0} top`,
        end: () => `+=${(n - 1) * STEP()}`,
        pin: true,
        invalidateOnRefresh: true,
        /* Imán entre fotos: si se para el scroll a medio cambio, se coloca en
           la foto más cercana. Solo actúa al parar; el gesto siempre manda */
        snap: {
          snapTo: 1 / (n - 1),
          duration: { min: 0.25, max: 0.6 },
          delay: 0.12,
          ease: 'power2.inOut',
          inertia: false,
        },
        onUpdate: (self) => {
          const i = Math.min(n - 1, Math.floor(self.progress * (n - 1) + 0.5));
          if (jumping === null && i !== shown) {
            root._storyGo?.(i, i > shown ? 1 : -1);
            shown = i;
          }
        },
      });
      const at = (i) => st.start + ((st.end - st.start) * i) / (n - 1);
      /* Imán de ENTRADA (Guille, 30/09): la animación arrancaba con el módulo
         a medio entrar. En el último tramo antes de fijarse, si se para el
         scroll con más de un 20 % del camino hecho, termina de colocarlo justo
         donde se fija. Con menos, no toca nada: no tira de quien solo mira.
         Solo hacia delante. */
      const approach = ScrollTrigger.create({
        start: () => Math.max(0, st.start - window.innerHeight * 0.8),
        end: () => st.start,
        invalidateOnRefresh: true,
        snap: {
          snapTo: (v) => (v > 0.2 ? 1 : v),
          duration: { min: 0.35, max: 0.8 },
          delay: 0.15,
          ease: 'power2.inOut',
          inertia: false,
        },
      });
      root._storyJump = (i, done) => {
        if (i !== shown) root._storyGo?.(i, i > shown ? 1 : -1);
        shown = i;
        jumping = i;
        glide(at(i), () => { jumping = null; done?.(); });
      };
      /* Los productos: la parada que cierra el recorrido guiado. Si no hay
         reel, el módulo que venga después */
      const afterTop = () => {
        const target = document.querySelector('.product-reel')?.closest('.shopify-section')
          || root.closest('.shopify-section')?.nextElementSibling;
        const y = target ? target.getBoundingClientRect().top + window.scrollY : st.end + 2;
        return Math.max(y, st.end + 2);
      };
      const skip = root.querySelector('[data-story-skip]');
      /* «Saltar»: el módulo siguiente, arriba de la pantalla (y no el final
         del tramo, que lo dejaba asomando abajo) */
      const onSkip = () => glide(afterTop());
      if (skip) {
        skip.hidden = false;
        skip.addEventListener('click', onSkip);
      }
      /* Sus paradas para la guía de scroll general (más abajo): una por foto */
      root._storySteps = () => Array.from({ length: n }, (_, i) => at(i));
      made.push(() => {
        approach.kill();
        delete root._storySteps;
        delete root._storyJump;
        if (skip) { skip.hidden = true; skip.removeEventListener('click', onSkip); }
      });
    });
    /* Al salir de escritorio (o activar motion reducido), todo vuelve al slider normal */
    return () => made.forEach((undo) => undo());
  });

  /* MÓDULO · brand-shop (data-shop-steps) — «Granola dentro, problemas fuera»
     El módulo se queda fijo y cada tramo de scroll cambia las dos frases (un
     paso = frase izquierda + derecha); el chico, las pegatinas y las flechas
     no se mueven. Como el story-slider por pasos: el paso sale de la posición,
     no de un gesto, y el imán solo actúa al parar. Solo escritorio y con
     motion: en móvil, o sin JS, se queda el primer paso. */
  gsap.matchMedia().add('(min-width: 750px) and (prefers-reduced-motion: no-preference)', () => {
    const made = [];
    document.querySelectorAll('[data-shop-steps]').forEach((root) => {
      const n = Number(root.dataset.shopSteps) || 0;
      if (n < 2) return;
      const phrases = root.querySelectorAll('[data-shop-step]');
      let shown = 0;
      const show = (i) => {
        /* Solo a la vista: los otros pasos siguen para el lector de pantalla */
        phrases.forEach((p) => p.classList.toggle('is-active', Number(p.dataset.shopStep) === i));
        shown = i;
      };
      show(0);
      const st = ScrollTrigger.create({
        trigger: root,
        /* Fijo con el fondo JUSTO bajo la navbar (sticky) y su onda de entrada
           por encima: fijado en top 0, la barra le tapaba el lazo del titular */
        start: () => `top+=${root.querySelector('.brand-shop__wave')?.offsetHeight || 0} top+=${document.querySelector('.site-header')?.offsetHeight || 0}`,
        end: () => `+=${(n - 1) * window.innerHeight * token('--motion-story-step', 0.55)}`,
        pin: true,
        invalidateOnRefresh: true,
        snap: {
          snapTo: 1 / (n - 1),
          duration: { min: 0.25, max: 0.6 },
          delay: 0.12,
          ease: 'power2.inOut',
          inertia: false,
        },
        onUpdate: (self) => {
          const i = Math.min(n - 1, Math.floor(self.progress * (n - 1) + 0.5));
          if (i !== shown) show(i);
        },
      });
      /* Sus paradas para la guía de scroll general: una por paso */
      root._shopStops = () => Array.from({ length: n }, (_, i) => st.start + ((st.end - st.start) * i) / (n - 1));
      made.push(() => {
        st.kill();
        delete root._shopStops;
        phrases.forEach((p) => p.classList.toggle('is-active', p.dataset.shopStep === '0'));
      });
    });
    return () => made.forEach((undo) => undo());
  });

  /* GUÍA DE SCROLL entre módulos (Guille, 30/09: «una tónica general de
     navegación, para guiar al usuario entre los módulos y que las animaciones
     no queden a mitad»). Solo escritorio y con motion.
     · Hacia ABAJO, cada gesto lleva a la siguiente parada:
       - el principio de cada módulo (así su animación de entrada arranca
         con él encuadrado);
       - los pasos de un módulo fijo por pasos (story-slider por scroll);
       - las piezas finas (bandanas, ondas sueltas) no son parada.
     · Un módulo MÁS ALTO que la pantalla (Moods, el pie) se recorre con
       scroll libre, y al llegar a su final el siguiente gesto lleva al de
       después.
     · Hacia ARRIBA, siempre natural; al volver a bajar se retoma desde la
       parada siguiente a donde se esté.
     · Un gesto = un paso: la inercia del trackpad sigue mandando eventos
       tras el salto; hasta que el gesto no acaba no hay otro salto. */
  gsap.matchMedia().add('(min-width: 750px) and (prefers-reduced-motion: no-preference)', () => {
    /* ⭐ En la FICHA de producto no hay guía: es página de conversión y el
       usuario compara, vuelve a la galería, lee ingredientes… navega libre
       (Guille, 02/10). Mismo criterio que los módulos de producto. */
    if (document.body.dataset.template === 'product') return;
    /* ⭐ LA GUÍA SOLO EN LA HOME Y EN LAS PÁGINAS DE CONTENIDO DE MARCA (Guille,
       06/10): B Corp, The With Club, La Newyorkina, Movimiento with, El equipo
       y For Business, que son relato por módulos. En el resto se navega
       libre: tienda y colecciones, cesta, blog, artículos y recetas (se leen
       de corrido), contacto, FAQ, legales y buscador. Una plantilla nueva
       nace SIN guía; si es de contenido, se añade aquí su sufijo.
       Los enlaces a un ancla siguen deslizándose en todas. */
    const GUIDED_PAGES = ['b-corp', 'with-club', 'la-newyorkina', 'movimiento-with', 'el-equipo', 'for-business'];
    const { template, templateSuffix } = document.body.dataset;
    const guided = template === 'index' || (template === 'page' && GUIDED_PAGES.includes(templateSuffix));
    const glideTo = (y, done) => {
      const o = { y: window.scrollY };
      /* Tramos largos (el tapado de la cabecera, ~1000 px) con algo más de
         tiempo: a 0,9 s fijos se leían como un salto */
      const duration = gsap.utils.clamp(0.9, 1.4, Math.abs(y - o.y) / 800);
      return gsap.to(o, { y, duration, ease: 'power2.inOut', onUpdate: () => window.scrollTo(0, o.y), onComplete: done });
    };

    /* Enlaces a un ancla de la misma pagina («Descubrir» de la cabecera, el
       «sigue» de una historia): deslizamiento con el mismo tween, no salto.
       ⚠️ No con `scroll-behavior: smooth` en CSS: suavizaria tambien los
       scrollTo por fotograma de este tween y de la guia. */
    const onAnchor = (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || link.hasAttribute('data-smile-panel') || event.defaultPrevented) return;
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      const target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      const max = document.documentElement.scrollHeight - window.innerHeight;
      glideTo(Math.min(target.getBoundingClientRect().top + window.scrollY, max), () => history.replaceState(null, '', '#' + id));
    };
    document.addEventListener('click', onAnchor);
    const map = () => {
      const vh = window.innerHeight;
      const stops = [0];
      const talls = [];
      const frees = [];
      document.querySelectorAll('.shopify-section').forEach((section) => {
        if (section.closest('.shopify-section-group-header-group')) return;
        const r = section.getBoundingClientRect();
        if (!r.height) return;
        const top = Math.round(r.top + window.scrollY);
        /* ⭐ Producto (rejilla de colección o reel/carrusel): se navega LIBRE
           mientras estés dentro —el usuario compara, vuelve, arrastra—; la
           guía no lo empuja al módulo siguiente (Guille, 02/10). Su principio
           sigue siendo parada para llegar a él. */
        if (section.querySelector('.product-grid, .product-reel, [data-free-scroll]')) {
          frees.push({ top, bottom: top + r.height });
          stops.push(top);
          return;
        }
        const story = section.querySelector('[data-story-scroll]');
        if (story?._storySteps) {
          story._storySteps().forEach((y, i) => stops.push({ y, story, i }));
          return;
        }
        /* Foto que se expande (talent-card): llega enmarcada y el siguiente
           gesto la expande; el siguiente, al módulo de después */
        /* Cabecera con vídeo/foto fijada (hero-video): parada en el marco; el
           siguiente gesto, al módulo que la tapa. No cuenta como módulo alto
           aunque su pin-spacer lo sea. */
        const pinnedHero = section.querySelector('.hvs');
        if (pinnedHero?._guideStops) {
          stops.push(top);
          pinnedHero._guideStops().forEach((y) => stops.push({ y: Math.round(y), keep: true }));
          return;
        }
        /* Shop con frases por pasos (brand-shop): una parada por paso */
        const shop = section.querySelector('[data-shop-steps]');
        if (shop?._shopStops) {
          shop._shopStops().forEach((y) => stops.push({ y: Math.round(y) }));
          return;
        }
        const expand = section.querySelector('.talent-card');
        if (expand?._expandStops) {
          expand._expandStops().forEach((y) => stops.push({ y: Math.round(y) }));
          return;
        }
        if (r.height < vh * 0.4) return;          /* pieza fina: no es parada */
        stops.push(top);
        /* Alto de verdad: más de un 25 % sobre la pantalla (el reel, con un 17 %
           de más, se recorría libre y descuadraba el siguiente) */
        if (r.height > vh * 1.25) talls.push({ top, bottom: top + r.height });
      });
      const sorted = stops.map((stop) => (typeof stop === 'number' ? { y: stop } : stop)).sort((a, b) => a.y - b.y);
      /* Paradas casi pegadas (arriba del todo y el borde del hero, a 113 px)
         son la misma: se queda la primera, salvo que sea un paso de un módulo */
      const list = sorted.filter((stop, k) => stop.story || stop.keep || k === 0 || stop.y - sorted[k - 1].y > vh * 0.3);
      return { list, talls, frees, vh };
    };
    /* ⚠️ `busy` = hay un deslizamiento EN CURSO, y se suelta en cuanto acaba
       (11/10). Antes no se soltaba hasta que hubiera 220 ms sin eventos, y
       con una tecla mantenida o una rueda que no para ese silencio no llega
       nunca: la página se quedaba clavada en la parada (ver onWheel). */
    let busy = false;
    let tween = null;
    let lastInput = 0;
    let glideEnd = 0;
    let pushes = 0;
    const release = () => {
      busy = false;
      tween = null;
      glideEnd = Date.now();
      pushes = 0;
    };
    /* Un gesto EN CONTRA (rueda o tecla hacia arriba) corta el deslizamiento:
       si no, el tween sigue escribiendo el scroll hasta su parada y el gesto
       del usuario no cuenta hasta que termina (se lee como un rebote). */
    const cancel = () => {
      if (!tween) return;
      tween.kill();
      release();
    };
    const guideDown = () => {
      const y = window.scrollY;
      const { list, talls, frees, vh } = map();
      /* Dentro de un módulo de producto —o con él ya en la mitad de arriba de
         la pantalla, que es el caso de la Tienda, donde empieza casi arriba—:
         libre hasta salir de él */
      if (frees.some((z) => y + vh * 0.5 > z.top && y < z.bottom - 4)) return false;
      /* Dentro de un módulo alto y sin haber llegado a su final: libre */
      if (talls.some((t) => y >= t.top - 4 && y + vh < t.bottom - 4)) return false;
      if (busy) return true;
      const next = list.find((stop) => stop.y > y + 4);
      if (!next) return false;
      const max = document.documentElement.scrollHeight - vh;
      busy = true;
      if (next.story && next.story._storyJump) next.story._storyJump(next.i, release);
      else tween = glideTo(Math.min(next.y, max), release);
      return true;
    };
    /* El gesto que provocó un salto se consume ENTERO: su inercia llegaba
       después del salto y, si el destino era un módulo alto (scroll libre
       dentro), lo movía 36 px de más. Un gesto termina con 220 ms de silencio…
       ⚠️ …O CON UN EMPUJÓN NUEVO (11/10). Solo con el silencio, la página se
       ATASCABA en la parada (el slider de bienvenida es la primera, y ahí se
       notaba): varios gestos de trackpad seguidos se encadenan por la inercia
       y una rueda de ratón girando manda eventos sin parar, así que los 220 ms
       no llegaban nunca y se tragaba TODO lo que viniera detrás. La inercia
       solo decae; si, ya terminado el deslizamiento, llegan dos eventos
       seguidos que NO decaen y con cuerpo (≥ PUSH_MIN px), es el usuario
       empujando otra vez: gesto nuevo. Y como red, pasado TAIL_MAX desde que
       acabó el deslizamiento ya no puede ser la cola de aquella inercia. */
    const PUSH_MIN = 12;
    const TAIL_MAX = 2000;
    let gestureGuided = false;
    let lastDelta = 0;
    const onWheel = (event) => {
      if (!event.deltaY || event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      if (event.deltaY < 0) {
        cancel();
        gestureGuided = false;
        return;
      }
      const now = Date.now();
      /* En píxeles, venga en la unidad que venga (Firefox con ratón: líneas) */
      const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1);
      pushes = delta >= PUSH_MIN && delta >= lastDelta ? pushes + 1 : 0;
      const pushed = !busy && (pushes >= 2 || (now - glideEnd > TAIL_MAX && delta >= 4));
      if (now - lastInput > 220 || pushed) gestureGuided = false;
      lastInput = now;
      lastDelta = delta;
      if (gestureGuided) { event.preventDefault(); return; }
      if (guideDown()) { gestureGuided = true; pushes = 0; event.preventDefault(); }
    };
    const onKey = (event) => {
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) { cancel(); return; }
      if (!['ArrowDown', 'PageDown', ' ', 'Spacebar'].includes(event.key) || event.shiftKey) return;
      if (event.target.closest?.('input, textarea, select, [contenteditable], button, a, [role="tab"], [data-reel-stage]')) return;
      lastInput = Date.now();
      /* Tecla mantenida: un respiro en cada parada antes de seguir a la siguiente */
      if (event.repeat && !busy && Date.now() - glideEnd < 300) { event.preventDefault(); return; }
      if (guideDown()) event.preventDefault();
    };
    /* Con un overlay abierto (menú, carrito, buscador) no se guía nada */
    const open = () => document.documentElement.classList.contains('overflow-hidden') || document.body.style.overflow === 'hidden' || !!document.querySelector('dialog[open]');
    const wheel = (event) => { if (!open()) onWheel(event); };
    const key = (event) => { if (!open()) onKey(event); };
    if (guided) {
      window.addEventListener('wheel', wheel, { passive: false });
      window.addEventListener('keydown', key);
    }
    return () => {
      window.removeEventListener('wheel', wheel);
      window.removeEventListener('keydown', key);
      document.removeEventListener('click', onAnchor);
    };
  });

  gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
    /* Parallax: la foto viaja dentro del marco; el scale evita que asomen
       bordes. El marco recorta (overflow: clip en el contenedor). */
    /* ⚠️ 30/09/2026: con 2 puntos de margen sobre el recorrido y escalando
       desde el CENTRO. Con el escalado justo (1 + 2·drift), en el extremo del
       recorrido el borde de la foto coincidía con el del marco y asomaba una
       línea de 1 px; y en las <image> SVG GSAP escala desde la esquina de
       arriba a la izquierda y destapaba el borde de arriba. Se mueven TODAS
       las fotos del marco (un slider puede llevar varias). */
    const PARALLAX_SCALE = 1 + (DRIFT * 2 + 2) / 100;
    document.querySelectorAll('[data-motion="parallax"]').forEach((frame) => {
      const media = frame.querySelectorAll('img, image');
      if (!media.length) return;
      gsap.fromTo(
        media,
        { yPercent: -DRIFT, scale: PARALLAX_SCALE, transformOrigin: '50% 50%' },
        {
          yPercent: DRIFT,
          scale: PARALLAX_SCALE,
          ease: 'none',
          scrollTrigger: {
            trigger: frame,
            start: 'top bottom',
            end: 'bottom top',
            scrub: LAG,
          },
        }
      );
    });

    /* El giro de las pegatinas, en dos mitades que se suman en el CSS:
       - BALANCEO: dónde está el elemento en pantalla, de -SPIN a +SPIN.
       - EMPUJE: lo rápido que scrolleas. Bajas lento, gira lento; bajas rápido,
         gira rápido; paras y vuelve solo a su sitio.

       ⚠️ AQUÍ GSAP SOLO MIDE, no anima. Animar `transform` no vale: al hacerlo
       GSAP escribe `rotate: none; translate: none; scale: none` inline para
       tomar control exclusivo de las transformaciones, y eso se lleva por
       delante la inclinación propia de cada silueta (los 17° del sello). Así que
       el ángulo se compone entero en `rotate` con calc(), y lo único que hace el
       JS es escribir sus dos sumandos como custom properties.

       El empuje INTEGRA la velocidad en vez de asignarla (`+=`, no `=`), que es
       lo que hace que "rápido" sea velocidad de giro y no un ángulo fijo, y el
       decay lo frena hacia cero, así que el sistema se estabiliza solo y nunca
       acumula de más. Conserva el signo: subiendo gira al revés.

       El empuje es UNO para todas —la velocidad es de la página, no de cada
       pegatina—, así que va en :root: un write por frame en total. */
    /* orbit: la silueta de la pegatina como la CADENA DE UN TANQUE (Guille,
       30/09): la forma no gira entera desde su centro, son sus pétalos los que
       avanzan por el contorno. Se redibuja en cada fotograma: una base (un
       círculo para la flor, un estadio para la nube) con festones en arco
       —unión de círculos, como las siluetas del Figma— cuyo arranque (la fase)
       avanza por el perímetro. Sola va despacio; con scroll acelera y al parar
       vuelve a su ritmo (el empuje y la frenada de la bandana). Alternan el
       sentido. Solo se dibuja lo que está en pantalla. */
    const orbits = gsap.utils.toArray('[data-motion="orbit"]');
    if (orbits.length) {
      const BASE = token('--motion-orbit-speed', 14);    /* px de contorno por segundo, en reposo */
      const BOOST = token('--motion-orbit-boost', 0.12); /* px/s extra por cada px/s de scroll */
      const DECAY = 0.92;
      const SAMPLES = 240;
      /* Geometría de cada silueta: base redonda (flor) o alargada (nube), alto
         del festón respecto al alto y cuántos festones caben */
      const geometry = (el, w, h) => {
        if (el.classList.contains('sticker--seal')) {
          const amp = h * 0.13;
          return { amp, stretch: true, period: amp * 2.3 };
        }
        /* Flor: 8 pétalos redondos (bloom) o 10 (flower) */
        const petals = el.classList.contains('sticker--flower') ? 10 : 8;
        const R = Math.min(w, h) / 2;
        const amp = R * (petals === 8 ? 0.27 : 0.22);
        return { amp, stretch: false, petals };
      };
      /* Punto y normal a la distancia s del contorno de un estadio (con largo
         recto 0 es un círculo). Empieza arriba a la izquierda, en sentido horario. */
      const stadium = (cx, cy, straight, r, s) => {
        const half = straight / 2;
        const arc = Math.PI * r;
        if (s < straight) return [cx - half + s, cy - r, 0, -1];
        s -= straight;
        if (s < arc) { const t = -Math.PI / 2 + s / r; return [cx + half + r * Math.cos(t), cy + r * Math.sin(t), Math.cos(t), Math.sin(t)]; }
        s -= arc;
        if (s < straight) return [cx + half - s, cy + r, 0, 1];
        s -= straight;
        const t = Math.PI / 2 + s / r;
        return [cx - half + r * Math.cos(t), cy + r * Math.sin(t), Math.cos(t), Math.sin(t)];
      };
      const tracks = orbits.map((el, i) => {
        const svg = el.querySelector('.sticker__track');
        return svg && { el, svg, path: svg.querySelector('path'), phase: 0, dir: i % 2 ? -1 : 1, visible: false, g: null };
      }).filter(Boolean);
      const measure = (t) => {
        const w = t.el.offsetWidth, h = t.el.offsetHeight;
        if (!w || !h) return;
        const g = geometry(t.el, w, h);
        const r = h / 2 - g.amp;
        const straight = g.stretch ? Math.max(0, w - h) : 0;
        const rr = g.stretch ? r : Math.min(w, h) / 2 - g.amp;
        const L = 2 * straight + 2 * Math.PI * rr;
        const count = g.stretch ? Math.max(6, Math.round(L / g.period)) : g.petals;
        t.g = { w, h, amp: g.amp, r: rr, straight, L, P: L / count };
        t.svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      };
      const draw = (t) => {
        const { w, h, amp, r, straight, L, P } = t.g;
        let d = '';
        for (let k = 0; k < SAMPLES; k++) {
          const s = (L * k) / SAMPLES;
          const u = ((((s - t.phase) % P) + P) % P) / P;
          const off = amp * Math.sqrt(Math.max(0, 1 - (2 * u - 1) ** 2));
          const [x, y, nx, ny] = stadium(w / 2, h / 2, straight, r, s);
          d += `${k ? 'L' : 'M'}${(x + nx * off).toFixed(1)},${(y + ny * off).toFixed(1)}`;
        }
        t.path.setAttribute('d', `${d}Z`);
      };
      tracks.forEach((t) => { measure(t); if (t.g) { draw(t); t.el.classList.add('is-tracking'); } });
      ScrollTrigger.addEventListener('refresh', () => tracks.forEach(measure));
      const watch = new IntersectionObserver((entries) => entries.forEach((entry) => {
        const t = tracks.find((x) => x.el === entry.target);
        if (t) t.visible = entry.isIntersecting;
      }));
      tracks.forEach((t) => watch.observe(t.el));
      let push = 0;
      let lastY = window.scrollY;
      gsap.ticker.add((time, deltaTime) => {
        const dt = Math.min(deltaTime, 64) / 1000;
        if (!dt) return;
        const y = window.scrollY;
        const speed = Math.abs(y - lastY) / dt;
        lastY = y;
        push = push * DECAY + speed * BOOST * (1 - DECAY);
        tracks.forEach((t) => {
          if (!t.visible || !t.g) return;
          t.phase = (t.phase + t.dir * (BASE + push) * dt) % t.g.L;
          draw(t);
        });
      });
    }

    const spinTargets = gsap.utils.toArray('[data-motion="spin"]');
    if (spinTargets.length) {
      const KICK_GAIN = token('--motion-spin-velocity-gain', 0.0005);
      const KICK_MAX = token('--motion-spin-velocity-max', 24);
      const KICK_DECAY = token('--motion-spin-velocity-decay', 0.94);
      const SWING_EASE = token('--motion-spin-swing-ease', 0.12);
      const root = document.documentElement;
      let velocity = 0;
      let angle = 0;
      let painted = 0;

      /* El balanceo persigue a su objetivo con un lerp en vez de ir clavado al
         scroll: es el mismo retardo que daba el `scrub` de ScrollTrigger. */
      const swings = spinTargets.map((el) => ({ el, target: 0, current: 0, painted: 0 }));
      swings.forEach((swing) => {
        ScrollTrigger.create({
          trigger: swing.el,
          start: 'top bottom',
          end: 'bottom top',
          onUpdate: (self) => {
            swing.target = gsap.utils.interpolate(-SPIN, SPIN, self.progress);
          },
        });
      });

      ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate: (self) => {
          velocity = self.getVelocity();
        },
      });

      gsap.ticker.add(() => {
        angle = (angle + velocity * KICK_GAIN) * KICK_DECAY;
        angle = gsap.utils.clamp(-KICK_MAX, KICK_MAX, angle);
        /* La velocidad se consume: si el scroll para, ScrollTrigger deja de
           llamar a onUpdate y sin esto el último valor giraría para siempre. */
        velocity *= KICK_DECAY;
        const kick = Number(angle.toFixed(2));
        if (kick !== painted) {
          painted = kick;
          root.style.setProperty('--motion-spin-kick', `${kick}deg`);
        }

        swings.forEach((swing) => {
          swing.current += (swing.target - swing.current) * SWING_EASE;
          const next = Number(swing.current.toFixed(2));
          if (next === swing.painted) return;
          swing.painted = next;
          swing.el.style.setProperty('--sticker-swing', `${next}deg`);
        });
      });
    }

    /* La línea que se dibuja: crece de arriba abajo al ritmo del scroll.
       Para hilos narrativos. */
    document.querySelectorAll('[data-motion="line-draw"]').forEach((line) => {
      gsap.fromTo(
        line,
        { scaleY: 0, transformOrigin: 'top center' },
        {
          scaleY: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: line,
            start: 'top 85%',
            end: 'bottom 55%',
            scrub: LAG,
          },
        }
      );
    });

    /* Reveal genérico en cascada: cards, features, cualquier bloque.

       ⭐ La onda entra CON su seccion, como una sola pieza. Cada modulo saca su
       onda justo antes de su `<section>`, asi que son hermanas: si solo se anima
       la seccion, baja ella y la onda se queda quieta — y durante la entrada se
       ve el corte entre las dos. La onda es el borde superior del modulo, no un
       adorno suelto, y tiene que moverse con el. */
    const conSuOnda = (el) => {
      const grupo = [el];
      const prev = el.previousElementSibling;
      if (prev && prev.matches && prev.matches('.liquid-divider')) grupo.unshift(prev);
      return grupo;
    };
    /* Las tarjetas del equipo con morph no: su entrada es la flor que se abre,
       y su posición la lleva la profundidad (dos animaciones sobre la misma y) */
    const revealTargets = gsap.utils.toArray('[data-motion="reveal"]').filter((el) => !el.closest('[data-team-morph]'));
    if (revealTargets.length) {
      gsap.set(revealTargets.flatMap(conSuOnda), { autoAlpha: 0, y: DISTANCE });
      ScrollTrigger.batch(revealTargets, {
        start: enterAt('top 70%'),
        once: true,
        /* El stagger va a mano —`delay` por indice— porque cada elemento del
           lote anima ahora un GRUPO y no un elemento: con `stagger` la onda y su
           seccion contarian como dos pasos y entrarian desacompasadas. */
        onEnter: (els) =>
          els.forEach((el, i) =>
            gsap.to(conSuOnda(el), {
              autoAlpha: 1,
              y: 0,
              duration: DURATION,
              ease: EASE,
              delay: i * 0.12,
            })
          ),
      });
    }

    /* El CTA cierra la secuencia (regla 5 del manual): mismo reveal, pero
       siempre por detrás del bloque que lo precede. No basta con que esté
       más abajo en el DOM — si el módulo entero entra de golpe en pantalla,
       el batch genérico lo revelaría a la vez que el arte y el titular. */
    const ctaTargets = gsap.utils.toArray('[data-motion="reveal-cta"]');
    if (ctaTargets.length) {
      gsap.set(ctaTargets, { autoAlpha: 0, y: DISTANCE });
      const revealCta = (els) =>
        gsap.to(els, {
          autoAlpha: 1,
          y: 0,
          duration: DURATION,
          ease: EASE,
          delay: 0.45,
          stagger: 0.12,
          overwrite: true,
        });
      ScrollTrigger.batch(ctaTargets, {
        start: enterAt('top 70%'),
        once: true,
        onEnter: revealCta,
      });
      /* ⚠️ RED DE SEGURIDAD (29/09): un botón invisible es un botón roto. Al
         llegar de un salto (ancla, Inicio/Fin, recarga a media página) el
         batch puede no dispararse; si el CTA lleva 1,2 s en pantalla y sigue
         oculto, aparece igual. */
      const ctaWatch = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        setTimeout(() => {
          if (parseFloat(getComputedStyle(el).opacity) < 0.5) revealCta([el]);
          ctaWatch.unobserve(el);
        }, 1200);
      }), { threshold: 0.5 });
      ctaTargets.forEach((el) => ctaWatch.observe(el));
    }

    /* Titular del hero: las palabras entran DESLIZANDOSE en horizontal, una
       detras de otra, y cuando ha entrado la ultima se traza el subrayado.

       ⭐ El orden importa: primero se lee la frase entera y DESPUES aparece la
       marca sobre la palabra que la remata. Trazarla a la vez que entra el
       texto la convertiria en decoracion; al final, es un subrayado de verdad.

       ⚠️ Espera a document.fonts.ready igual que el resto de SplitText: con
       Explorer cargando en `swap`, medir antes parte las palabras donde no es. */
    document.fonts.ready.then(() => {
      document.querySelectorAll('[data-motion="hero-title"]').forEach((el) => {
        const marca = el.querySelector('.mk-underline svg');

        /* ⚠️ El trazo nace SIN dibujar desde el JS y no desde el CSS: si el
           script no llegara a correr, un `clip-path` escrito en la hoja lo
           dejaria invisible para siempre. */
        if (marca) gsap.set(marca, { clipPath: 'inset(-40% 100% -40% -5%)' });

        /* ⚠️ SIN `mask: 'words'`. La mascara mete cada palabra en una caja con
           overflow oculto, y una CURSIVA se sale de la suya: a «with» le
           cortaba la H final. Sin mascara se entra con opacidad, que ademas
           deja el desplazamiento mas suelto. */
        const split = SplitText.create(el, { type: 'words' });

        const tl = gsap.timeline({
          scrollTrigger: { trigger: el, start: enterAt('top 85%'), once: true },
        });

        /* Mas lento que el reveal del sistema y con el desplazamiento en X:
           entran desde la derecha, que es como se lee. */
        tl.from(split.words, {
          xPercent: 60,
          autoAlpha: 0,
          duration: 1.1,
          ease: EASE,
          stagger: 0.14,
        });

        if (marca) {
          tl.to(marca, {
            clipPath: 'inset(-40% -5% -40% -5%)',
            duration: 0.7,
            ease: 'power2.inOut',
          }, '+=0.15');
        }
      });
    });

    /* Text reveal por líneas. Espera a las fuentes: SplitText mide líneas. */
    document.fonts.ready.then(() => {
      document.querySelectorAll('[data-motion="reveal-text"]').forEach((el) => {
        const split = SplitText.create(el, { type: 'lines', mask: 'lines' });
        gsap.from(split.lines, {
          yPercent: 110,
          duration: DURATION,
          ease: EASE,
          stagger: 0.09,
          scrollTrigger: { trigger: el, start: enterAt('top 75%'), once: true },
        });
      });
      ScrollTrigger.refresh();
    });

    /* Lines: el texto sube línea a línea desde su máscara LIGADO AL SCROLL
       (scrub), como la demo de GSAP «Responsive line splits on scroll»
       (codepen GreenSock/GggpRoB). Para textos de cuerpo: cada línea tarda lo
       que tarda en cruzar su trozo de pantalla, así que se lee al ritmo del
       usuario. `autoSplit` rehace el corte si cambia el ancho (las líneas
       dependen de él) y `onSplit` devuelve la animación para que GSAP la
       recree con el corte nuevo. `clamp()` evita que un texto que ya está en
       pantalla al cargar arranque a medias.
       ⚠️ En textos con marcas de rotulador (.mk) no: el corte en líneas mete
       cada palabra en su caja y la marca pierde su referencia. */
    document.fonts.ready.then(() => {
      /* data-lines-each: el elemento es un contenedor de texto enriquecido
         (varios <p>) y cada hijo se parte POR SEPARADO. Partido entero, las
         líneas quedaban como hijas directas del contenedor y el hueco entre
         párrafos se aplicaba entre LÍNEAS (story-slider, 30/09/2026). */
      /* Una lista se parte POR PUNTO (cada <li> es su unidad), no la lista
         entera: partida de golpe, las líneas envolvían a los <li> y cada línea
         salía con su viñeta (B Corp, «Qué significa ser B Corp») */
      const units = [...document.querySelectorAll('[data-motion="lines"]')]
        .flatMap((el) => (el.hasAttribute('data-lines-each') ? [...el.children] : [el]))
        .flatMap((el) => (/^(UL|OL)$/.test(el.tagName) ? [...el.children] : [el]));
      units.forEach((el) => {
        /* ⚠️ Lo que ya se ve al cargar (por encima del 90 % de la pantalla)
           (un subtítulo de hero) no se anima: su tramo de scroll mediría cero y
           las líneas se quedarían escondidas en la máscara. */
        if (el.getBoundingClientRect().top + scrollY < innerHeight * 0.9) return;
        let tweens = [];
        SplitText.create(el, {
          type: 'words,lines',
          mask: 'lines',
          linesClass: 'line',
          autoSplit: true,
          /* ⚠️ CADA LÍNEA CON SU PROPIO TRAMO (30/09/2026, Guille: en móvil
             «lo corta a mitad de pantalla»). Con un solo tramo para el
             párrafo entero (90 % → 55 %, con stagger), en un párrafo alto la
             última línea no llegaba hasta que el FINAL del párrafo tocaba
             media pantalla. Ahora cada línea sube cuando asoma ELLA, del 95 %
             al 75 % de la pantalla, mida lo que mida el párrafo. */
          onSplit: (instance) => {
            tweens.forEach((t) => { t.scrollTrigger?.kill(); t.kill(); });
            tweens = instance.lines.map((line, i) => gsap.from(line, {
              yPercent: 100,
              autoAlpha: 0,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: instance.masks?.[i] || line,
                scrub: LAG,
                start: 'top 95%',
                end: 'top 75%',
              },
            }));
          },
        });
      });
    });

    /* Moods: pegatinas que se pegan + cordón que une las paradas + puntero
       LNY viajero + dividers líquidos. Portado del prototipo aprobado
       (LNY-Brain docs/prototipos/moods-stickers.html). */
    document.querySelectorAll('[data-motion="moods"]').forEach((root) => {
      const section = root.querySelector('.moods');
      const cordSvg = root.querySelector('.moods__cord');
      const cordLine = cordSvg.querySelector('.cord-line');
      const cordReveal = cordSvg.querySelector('.cord-reveal');
      const pointer = root.querySelector('.moods__pointer');
      /* La punta de la flecha cae en (6,3) del viewBox 24 y apunta hacia
         (-5,-10): rotación base +117° para alinearla con la tangente. */
      const TIP_X = 0.25;
      const TIP_Y = 0.125;
      const POINTER_ANGLE = 117;
      let samples = [];   /* puntos del cordón (longitud → posición), los rehace buildCord */

      /* El cursor del sistema se apaga solo mientras el puntero de la escena
         se ve (ver el CSS de brand-moods): si no, al entrar por arriba parecía
         que no había ratón */
      let pointerInView = false;
      let pointerShown = false;
      const syncCursor = () => root.classList.toggle('is-pointer-live', pointerInView && pointerShown);
      if (pointer) {
        new IntersectionObserver((entries) => {
          pointerInView = entries[0].isIntersecting;
          syncCursor();
        }).observe(pointer);
      }

      /* Curva suave (Catmull-Rom → Bézier) por los puntos dados. */
      const catmullPath = (pts) => {
        let d = `M ${pts[0].x},${pts[0].y}`;
        for (let i = 0; i < pts.length - 1; i++) {
          const p0 = pts[Math.max(0, i - 1)];
          const p1 = pts[i];
          const p2 = pts[i + 1];
          const p3 = pts[Math.min(pts.length - 1, i + 2)];
          d += ` C ${p1.x + (p2.x - p0.x) / 6},${p1.y + (p2.y - p0.y) / 6}`
            + ` ${p2.x - (p3.x - p1.x) / 6},${p2.y - (p3.y - p1.y) / 6}`
            + ` ${p2.x},${p2.y}`;
        }
        return d;
      };

      const buildCord = () => {
        const rect = section.getBoundingClientRect();
        /* Parada = esquina inferior INTERIOR de la pegatina (pisándola un
           poco), medida por LAYOUT (rect del .mood + offsetWidth del sticker):
           el rect del sticker incluiría el scale del slap pendiente y el tilt,
           y desviaría los puntos. Impares → esquina derecha, y viceversa. */
        const stops = [...section.querySelectorAll('.mood')].map((mood, i) => {
          const m = mood.getBoundingClientRect();
          const sticker = mood.querySelector('.mood__sticker');
          const w = sticker.offsetWidth;
          const h = sticker.offsetHeight;
          const left = i % 2 === 0 ? m.left : m.right - w;
          const top = m.top + (m.height - h) / 2;
          const inward = w * 0.18;
          return {
            x: (i % 2 === 0 ? left + w - inward : left + inward) - rect.left,
            y: top + h * 0.88 - rect.top,
          };
        });
        if (stops.length < 2) return 0;
        /* Última parada: el CTA — la flecha aterriza en su lado derecho
           (zona del padding, sin tapar el texto). */
        const cta = section.querySelector('.moods__cta .button');
        /* En la disposición de página el módulo cierra con una ilustración
           ([data-cord-end]): el puntero aterriza en su borde de arriba. */
        const endArt = section.querySelector('[data-cord-end]');
        if (endArt) {
          const c = endArt.getBoundingClientRect();
          stops.push({ x: c.left + c.width * 0.42 - rect.left, y: c.top + c.height * 0.12 - rect.top });
        } else if (cta) {
          const c = cta.getBoundingClientRect();
          stops.push({
            x: c.right - c.height * 0.4 - rect.left,
            y: c.top + c.height * 0.45 - rect.top,
          });
        }
        /* Barriga curva entre paradas: punto medio desplazado en
           perpendicular, alternando lado. */
        const pts = [stops[0]];
        for (let i = 0; i < stops.length - 1; i++) {
          const a = stops[i];
          const b = stops[i + 1];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dist = Math.hypot(dx, dy) || 1;
          const amp = Math.min(dist * 0.22, 110) * (i % 2 ? 1 : -1);
          pts.push(
            { x: (a.x + b.x) / 2 - (dy / dist) * amp, y: (a.y + b.y) / 2 + (dx / dist) * amp },
            b
          );
        }
        cordSvg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
        const d = catmullPath(pts);
        cordLine.setAttribute('d', d);
        cordReveal.setAttribute('d', d);
        const len = cordReveal.getTotalLength();
        cordReveal.style.strokeDasharray = len;
        cordReveal.style.strokeDashoffset = len;
        /* Muestreo del cordón (longitud → punto): con él se busca qué punto
           del cordón cae a la altura de lectura */
        samples = [];
        for (let l = 0; l <= len; l += len / 500) samples.push({ l, p: cordLine.getPointAtLength(l) });
        return len;
      };

      let cordLen = buildCord();
      window.addEventListener('load', () => {
        cordLen = buildCord();
        ScrollTrigger.refresh();
      });

      /* Sticker-slap por mood. */
      section.querySelectorAll('.mood').forEach((mood) => {
        const sticker = mood.querySelector('.mood__sticker');
        const phrase = mood.querySelector('.mood__phrase');
        const tilt = parseFloat(getComputedStyle(mood).getPropertyValue('--tilt')) || 0;
        gsap.timeline({
          scrollTrigger: {
            trigger: mood,
            start: enterAt('top 78%'),
            toggleActions: 'play none none reverse',
            invalidateOnRefresh: true,
          },
        })
          .from(phrase, { y: 28, autoAlpha: 0, duration: 0.7, ease: 'power3.out' })
          .from(sticker, { scale: 1.65, rotate: tilt * 3.5, autoAlpha: 0, duration: 0.65, ease: 'back.out(1.8)' }, '-=0.35');
      });

      /* Cordón que se dibuja + puntero, LIGADOS AL SCROLL (Guille, 30/09: «¿no
         podemos animarlo con el scroll?»). Antes era un progreso con paradas
         propias en cada pegatina y parecía moverse solo. Ahora el puntero va
         donde LEES: el punto del cordón que cae a la altura de READ (60 % de
         la pantalla). Bajas, baja; te paras, se para; subes, retrocede. Las
         «paradas» salen solas: el cordón cruza en horizontal junto a cada
         pegatina. Con la inercia común (LAG) para que se deslice. */
      const READ = 0.6;
      const lengthAtY = (y) => {
        if (!samples.length) return 0;
        if (y <= samples[0].p.y) return 0;
        const hit = samples.find((sample) => sample.p.y >= y);
        return hit ? hit.l : cordLen;
      };
      const state = { L: 0 };
      const render = () => {
        const L = Math.max(0, Math.min(state.L, cordLen));
        cordReveal.style.strokeDashoffset = cordLen - L;
        pointer.style.visibility = L > 1 ? 'visible' : 'hidden';
        if (pointerShown !== L > 1) { pointerShown = L > 1; syncCursor(); }
        const pt = cordLine.getPointAtLength(L);
        const a = cordLine.getPointAtLength(Math.max(L - 2, 0));
        const b = cordLine.getPointAtLength(Math.min(L + 2, cordLen));
        const deg = (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
        gsap.set(pointer, {
          x: pt.x - pointer.offsetWidth * TIP_X,
          y: pt.y - pointer.offsetHeight * TIP_Y,
          rotation: deg + POINTER_ANGLE,
          transformOrigin: `${TIP_X * 100}% ${TIP_Y * 100}%`,
        });
      };
      const follow = () => {
        if (!cordLen) return;
        const line = innerHeight * READ - section.getBoundingClientRect().top;
        gsap.to(state, { L: lengthAtY(line), duration: LAG, ease: 'power3.out', overwrite: true, onUpdate: render });
      };
      ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        invalidateOnRefresh: true,
        onUpdate: follow,
        onRefresh: follow,
      });

      /* El cordon se dibuja desde las posiciones reales de las pegatinas, asi
         que hay que rehacerlo en cada refresh (fuentes, imagenes, pins de
         otras secciones que cambian la altura del documento...). El resize ya
         dispara refresh por si solo. */
      ScrollTrigger.addEventListener('refreshInit', () => {
        cordLen = buildCord();
      });
    });

    /* La onda se pinta sola: lee el fondo del modulo de arriba y el del de
       abajo y se colorea con ellos, para que en el editor no haya que
       acertar ningun color. Se recalcula si el editor recarga secciones. */
    const paintDividerColours = () => {
      const PAGE_BG = getComputedStyle(document.body).backgroundColor;
      const solid = (colour) => colour && colour !== 'rgba(0, 0, 0, 0)' && colour !== 'transparent';

      /* Color que SE VE en un borde concreto de un modulo —su filo de arriba o
         el de abajo—, que es lo que la onda tiene pegado a esa cara.

         ⭐ Manda la SUPERFICIE DECLARADA (`.surface--*`, la que se elige en el
         editor) y no la geometria. Se intento medir por geometria —bajar por el
         hijo que cubre el borde— y NO vale: las secciones con pin de
         ScrollTrigger montan un `pin-spacer` mucho mas alto que la seccion, asi
         que el borde inferior del contenedor cae en un hueco vacio; y ademas el
         pin se monta DESPUES de este calculo, de modo que se media un layout
         que todavia no existia. El manifiesto pineado daba blanco teniendo
         crema debajo, justo por eso.

         Se cogen solo las superficies de primer nivel (una tarjeta con su
         propia superficie dentro del modulo no es el fondo del modulo), y de
         ellas la primera o la ultima segun el borde: un modulo puede llevar dos
         bandas de distinto color, como el footer.

         ⚠️ Devuelve `null` cuando no encuentra nada, y esa distincion es la
         clave del arreglo: ANTES devolvia el fondo de PAGINA y el llamador lo
         leia como "no he encontrado". Servia mientras el fondo de pagina no
         fuese el de ninguna seccion, pero desde que `primary` ES el blanco de
         la pagina, CADA seccion blanca se leia como un fallo y caia al
         fallback, que devolvia crema. De ahi que casi toda onda que diese a un
         modulo blanco pintase su hueco en crema. */
      const bgAt = (root, edge) => {
        if (!root || !root.querySelectorAll) return null;
        /* El PIE visto desde arriba: lo primero que asoma es la banda del
           prefooter, no el granate del <footer> (que es la superficie
           declarada y ganaba). Con un modulo que remata con su propia onda
           —el Club en With Club— la del prefooter se oculta por apilada y la
           del modulo pintaba granate: franja granate recta sobre el crema
           (Guille, 01/10). */
        if (edge === 'top') {
          const pre = root.querySelector('.site-footer__newsletter');
          if (pre && pre.offsetHeight) {
            const colour = getComputedStyle(pre).backgroundColor;
            if (solid(colour)) return colour;
          }
        }
        const todas = root.matches('.surface') ? [root] : [...root.querySelectorAll('.surface')];
        const raiz = todas.filter((s) => !todas.some((o) => o !== s && o.contains(s)));
        if (raiz.length) {
          const el = edge === 'top' ? raiz[0] : raiz[raiz.length - 1];
          const colour = getComputedStyle(el).backgroundColor;
          if (solid(colour)) return colour;
        }
        /* Sin superficie declarada —el pie, la ficha de producto cuyo fondo es
           una mancha sacada de la foto— se baja por el primer o ultimo hijo
           segun el borde, y gana el fondo mas profundo QUE OCUPE EL ANCHO
           ENTERO.

           ⚠️ Ese matiz del ancho es el que reconcilia los dos casos, que tiran
           en direcciones contrarias: en la ficha, quedarse con el mas profundo
           cogia el blanco de las TARJETAS de ingredientes y metia una franja
           blanca entre la mancha naranja y el prefooter; en el pie, quedarse
           con el mas externo cogia el granate del `<footer>` cuando lo que
           asoma arriba es la banda crema del prefooter. Una banda ocupa todo el
           ancho y una tarjeta no: con eso se distinguen. */
        let node = root;
        let found = null;
        const anchoRoot = root.getBoundingClientRect().width;
        for (let depth = 0; node && depth < 6; depth++) {
          while (node && node.matches && node.matches('.liquid-divider')) {
            node = edge === 'top' ? node.nextElementSibling : node.previousElementSibling;
          }
          if (!node) break;
          const colour = getComputedStyle(node).backgroundColor;
          const ancho = node.getBoundingClientRect().width;
          if (solid(colour) && (anchoRoot === 0 || ancho >= anchoRoot * 0.9)) found = colour;
          const kids = [...node.children].filter((k) => !k.matches('.liquid-divider'));
          node = edge === 'top' ? kids[0] : kids[kids.length - 1];
        }
        return found;
      };

      document.querySelectorAll('[data-motion="liquid-divider"]').forEach((divider) => {
        const holder = divider.closest('.shopify-section');
        if (!holder) return;
        /* ⚠️ Si el divider ya trae su color decidido —el interior del footer,
           el del Club—, no se le toca: ese separa dos bloques de la MISMA
           seccion y este calculo mira secciones hermanas, donde no las hay. Lo
           que encontraba era el verde del sello ecologico de la banda. */
        if (divider.dataset.dividerFixed !== undefined) return;
        const standalone = !!divider.closest('.liquid-divider-section');

        /* Lo de ARRIBA es el filo INFERIOR del modulo anterior, y lo de ABAJO
           el filo SUPERIOR del siguiente: es lo que la onda tiene pegado a cada
           cara. Cuando la onda vive DENTRO de su seccion, uno de los dos lados
           es la propia seccion, y hay dos sitios de donde sacarlo —el hermano
           contiguo o el contenedor—, en ese orden. */
        let above;
        let below;
        const edge = divider.dataset.dividerEdge;
        if (edge) {
          /* Remate de una pieza a sangre —la foto del Club, la de la ficha—:
             la imagen esta EN MEDIO de la seccion, asi que solo una cara de la
             onda separa algo. Esa toma el color del modulo vecino y la otra se
             queda transparente para que la foto asome. */
          above = edge === 'top' ? bgAt(holder.previousElementSibling, 'bottom') : 'transparent';
          below = edge === 'bottom' ? bgAt(holder.nextElementSibling, 'top') : 'transparent';
        } else if (standalone) {
          // seccion Onda suelta: enlaza los dos modulos que tiene al lado
          above = bgAt(holder.previousElementSibling, 'bottom');
          below = bgAt(holder.nextElementSibling, 'top');
        } else if (!divider.previousElementSibling) {
          // borde superior de un modulo: del vecino de arriba al modulo
          above = bgAt(holder.previousElementSibling, 'bottom');
          below = bgAt(divider.nextElementSibling, 'top') ?? bgAt(holder, 'top');
        } else {
          // borde inferior: del modulo al vecino de abajo
          above = bgAt(divider.previousElementSibling, 'bottom') ?? bgAt(holder, 'bottom');
          below = bgAt(holder.nextElementSibling, 'top');
        }
        above = above ?? PAGE_BG;
        below = below ?? PAGE_BG;
        divider.style.setProperty('--divider-from', above);
        divider.style.setProperty('--divider-to', below);
      });
    };
    /* ⭐ Dos ondas seguidas son una de mas. Pasa cuando el modulo de arriba ya
       remata con la suya y el de abajo pone otra: entre las dos asoma una
       franja del color que no toca —en la home, una banda crema entre la foto
       del Club y el granate del testimonial—. Se queda la PRIMERA, que ya
       separa los dos modulos; la segunda solo repite el gesto.

       No se puede decidir en Liquid: ninguna seccion sabe con quien la van a
       colocar, y es justo el orden lo que crea el duplicado.

       ⚠️ Y NO se mide con rects, que fue el primer intento: dos secciones sin
       revelar todavia estan desplazadas por su propia animacion de entrada, asi
       que sus ondas no se tocan hasta que el scroll llega — y entonces ya es
       tarde. Se mira el DOM: si entre una onda y la siguiente no hay NADA que
       se vea, estan apiladas. Eso es cierto desde el primer frame. */
    const hayContenidoEntre = (a, b) => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
      walker.currentNode = a;
      let node = walker.nextNode();
      while (node && node !== b && !b.contains(node)) {
        if (a.contains(node)) {
          node = walker.nextNode();
          continue;
        }
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) return true;
        if (node.nodeType === Node.ELEMENT_NODE && node.matches('img, picture, video, canvas')) return true;
        node = walker.nextNode();
      }
      return false;
    };

    const hideStackedDividers = () => {
      const dividers = [...document.querySelectorAll('[data-motion="liquid-divider"]')];
      /* Solo se escribe cuando el valor CAMBIA: ocultar una onda mueve el
         layout, y esto tambien corre en el `refresh` de ScrollTrigger —
         escribir a ciegas en cada pasada seria pedir otro refresh cada vez. */
      const poner = (el, value) => {
        if (el.style.display !== value) el.style.display = value;
      };
      dividers.forEach((d) => poner(d, ''));
      for (let i = 0; i < dividers.length - 1; i++) {
        if (!hayContenidoEntre(dividers[i], dividers[i + 1])) {
          poner(dividers[i + 1], 'none');
        }
      }
    };

    const refreshDividers = () => {
      hideStackedDividers();
      paintDividerColours();
    };
    paintDividerColours();
    /* ⚠️ El apilado se mide con rects, asi que necesita el layout ya montado —y
       este script va con `defer`, asi que cuando corre el `load` PUEDE HABER
       PASADO YA y el listener no dispararia nunca. De ahi las dos ramas. */
    /* Varias pasadas y no una: el apilado se mide con rects, y en el arranque
       las fotos en diferido todavia no han asentado las alturas —con el Club a
       medio cargar sus dos ondas aun no se tocan y el par no se detecta—. Son
       tres lecturas baratas que ademas no escriben si nada ha cambiado. */
    const asentar = () => {
      requestAnimationFrame(refreshDividers);
      setTimeout(refreshDividers, 400);
      setTimeout(refreshDividers, 1400);
    };
    if (document.readyState === 'complete') {
      asentar();
    } else {
      window.addEventListener('load', asentar);
    }
    /* ⚠️ Y sobre todo aqui: el `refresh` de ScrollTrigger es el unico momento
       en que el layout es DEFINITIVO —imagenes cargadas, pines montados,
       alturas finales—. En el arranque las medidas todavia bailan y el par
       apilado del Club no se detectaba. */
    ScrollTrigger.addEventListener('refresh', refreshDividers);
    document.addEventListener('shopify:section:load', refreshDividers);
    document.addEventListener('shopify:section:reorder', refreshDividers);

    /* liquid-divider: onda liquida (portado del prototipo La Newyorkina v2, 30/09)
    La onda late con el scroll y chapotea al pasar el ratón (física de
    muelles). Los colores los pone el auto-color (variables CSS de la onda).
    GOTAS (PRUEBA, Guille 29/09: «como leche, un material fluido»): al pasar
    el ratón con velocidad, la onda suelta gotas. Onda normal (el líquido
    cuelga del módulo de arriba): gotean hacia abajo y se consumen. Onda
    volteada (el líquido sube desde el de abajo): salpican hacia arriba y
    vuelven a caer en la superficie. El aspecto líquido lo da un filtro
    «gooey» (desenfoque + umbral de alfa): lo que se toca se funde y, al
    separarse, se estira en un hilo que acaba soltándose. El filtro solo se
    enciende mientras hay gotas: en reposo la onda cuesta lo mismo que antes.
    Las gotas llevan el color de la onda, no un blanco «leche».
    MEMBRANA (Guille, 29/09: «que la primera vez tenga resistencia y luego
    lo atraviese»): el cursor del sistema no se puede retener, así que se
    resiste el MATERIAL. La primera vez que el ratón cruza el borde de una
    onda, la superficie se le pega como una película: se estira siguiéndolo
    con retraso (esa es la resistencia) y, al pasar de BREAK_PX, se rompe —
    vuelve con un rebote elástico, ondas y una ráfaga de gotas—. Cada onda
    tiene la suya y se REARMA a los REARM_S segundos de romperse (Guille: que
    no sea solo una vez); entre medias, el chapoteo y las gotas normales. */
    const loneDividers = [...document.querySelectorAll('[data-motion="liquid-divider"]')];
    if (loneDividers.length) {
      const W = 1440;
      const MID = 36;
      /* Cada variante es la misma fisica con otra longitud de onda: la grande
         respira despacio, la pequena hace festones. Ninguna se queda quieta. */
      const SHAPES = {
        /* n: 96 también en la grande (antes 24): la misma curva con más puntos,
           para que la punta estrecha de la membrana no caiga entre dos */
        large: { n: 96, wavelength: 180, amp: 9 },
        small: { n: 96, wavelength: 26, amp: 11 },
      };
      const N = 96;
      const smooth = (pts) => {
        let d = `M ${pts[0].x},${pts[0].y}`;
        for (let i = 0; i < pts.length - 1; i++) {
          const p0 = pts[Math.max(0, i - 1)];
          const p1 = pts[i];
          const p2 = pts[i + 1];
          const p3 = pts[Math.min(pts.length - 1, i + 2)];
          d += ` C ${p1.x + (p2.x - p0.x) / 6},${p1.y + (p2.y - p0.y) / 6}`
            + ` ${p2.x - (p3.x - p1.x) / 6},${p2.y - (p3.y - p1.y) / 6}`
            + ` ${p2.x},${p2.y}`;
        }
        return d;
      };
      const states = loneDividers.map(() => ({
        mx: W / 2,
        strength: 0,
        target: 0,
        prevX: null,
        y: new Float32Array(N + 1),
        v: new Float32Array(N + 1),
      }));
      const step = (st) => {
        const K = 0.045;
        const SPREAD = 0.12;
        const DAMP = 0.9;
        const MAX = 26;
        const { y, v } = st;
        for (let i = 0; i <= N; i++) {
          v[i] += -K * y[i]
            + SPREAD * ((i > 0 ? y[i - 1] - y[i] : 0) + (i < N ? y[i + 1] - y[i] : 0));
        }
        for (let i = 0; i <= N; i++) {
          v[i] *= DAMP;
          y[i] = Math.max(-MAX, Math.min(MAX, y[i] + v[i]));
        }
      };
      /* ⭐ CADA ONDA VIAJA EN SU PROPIO SENTIDO (24/08, peticion de Guille): antes
         el signo de `x / wavelength` era el mismo para todas, asi que TODAS
         viajaban hacia el mismo lado siempre — con dos ondas seguidas en una
         pagina (la pequena y la grande, o dos pequenas) eso se leia como una
         secuencia, un festón empujando al siguiente. `dir` invierte el sentido
         por indice de onda (par/impar), y como es un cambio de SIGNO y no solo
         de fase, la relacion entre dos ondas vecinas no es un desfase fijo que
         se repite igual siempre: se abre y se cierra con el tiempo, que es lo
         que da el efecto organico. El desfase de arranque (`phase`) se queda
         para que ademas no nazcan alineadas. */
      const wave = (time, kick, phase, dir, st, shape) => {
        const pts = [];
        const step = N / shape.n;
        for (let i = 0; i <= shape.n; i++) {
          const x = (W / shape.n) * i;
          const bump = Math.exp(-((x - st.mx) ** 2) / (2 * 170 ** 2)) * 9 * st.strength;
          const slosh = st.y[Math.round(i * step)];
          const swing = Math.sin(time + phase + dir * (x / shape.wavelength) * 1.05) * (shape.amp + kick);
          /* Estrecho: la película se estira hacia el cursor como un dedo, no como una ola */
          const pull = st.pull ? st.pull * Math.exp(-((x - st.px) ** 2) / (2 * 55 ** 2)) : 0;
          pts.push({ x, y: MID + swing + bump + slosh + pull });
        }
        st.edge = pts;
        /* El OTRO lado del borde (el color del módulo de abajo), dibujado como
           forma y no solo como fondo de la caja: si la membrana tira hacia
           arriba, la punta sale de la caja y el fondo ya no la pintaría. */
        /* Las dos formas son BANDAS que siguen al borde, no rectángulos que
           salen de la caja: fuera de la caja solo sobresale lo justo para el
           filtro y para la punta de la membrana. ⚠️ Con un cierre fijo lejos
           (-240), la onda volteada pintaba 240 px de crema sobre la historia. */
        const band = (dy, limit, pick) => pts.slice().reverse()
          .map((pt) => `L ${pt.x},${pick(pt.y + dy, limit).toFixed(1)}`).join(' ');
        if (st.below) {
          /* hacia abajo, hasta el fondo de la caja (el fondo de la caja pinta
             el resto); solo sale por arriba si la membrana tira hacia arriba */
          st.below.setAttribute('d', `${smooth(pts)} L ${W + 40},${pts[pts.length - 1].y} L ${W + 40},72 ${band(40, 72, Math.max)} L -40,72 L -40,${pts[0].y} Z`);
        }
        /* El lado líquido se cierra por fuera de la caja: 30 SOLO mientras el
           filtro de gotas está activo (su desenfoque se comería un borde recto
           dentro) y 1 en reposo. ⚠️ Con 30 siempre, la onda pintaba 30 px del
           color del módulo de arriba sobre su texto: cortaba la segunda línea
           de la dirección del footer (Guille, 30/09). Si la membrana lleva el
           borde más allá, la banda la acompaña 30 por encima de la punta; en
           reposo (borde a ~36) se queda en `out`. ⚠️ Con 60 fijos, la banda
           salía ~30 px por encima de la caja también en reposo. */
        const out = st.caught || st.goo?.hasAttribute('filter') ? -30 : -1;
        return `${smooth(pts)} L ${W + 40},${pts[pts.length - 1].y} L ${W + 40},${out} ${band(-30, out, Math.min)} L -40,${out} L -40,${pts[0].y} Z`;
      };

      /* ---------- Gotas ---------- */
      const SVG_NS = 'http://www.w3.org/2000/svg';
      if (!document.getElementById('lny-goo')) {
        const defs = document.createElementNS(SVG_NS, 'svg');
        defs.setAttribute('aria-hidden', 'true');
        defs.style.cssText = 'position:absolute;width:0;height:0';
        /* Un filtro por talla: la onda se estira en vertical (la pequeña es
           2,7 veces más baja en pantalla), así que el desenfoque vertical se
           compensa para que en pantalla sean ~7 px en las dos. */
        const goo = (id, sy) => `<filter id="${id}" x="-5%" y="-400%" width="110%" height="900%" color-interpolation-filters="sRGB">`
          + `<feGaussianBlur in="SourceGraphic" stdDeviation="7 ${sy}"/>`
          + '<feColorMatrix values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -8"/></filter>';
        defs.innerHTML = goo('lny-goo', 7) + goo('lny-goo-small', 18);
        document.body.append(defs);
      }
      const MAX_DROPS = 18;
      const edgeAt = (st, x) => {
        const pts = st.edge;
        if (!pts) return MID;
        const f = Math.max(0, Math.min(pts.length - 1, (x / W) * (pts.length - 1)));
        const i = Math.floor(f), t = f - i, b = pts[Math.min(i + 1, pts.length - 1)];
        return pts[i].y + (b.y - pts[i].y) * t;
      };
      loneDividers.forEach((zone, k) => {
        const st = states[k];
        const svg = zone.querySelector('svg');
        const path = zone.querySelector('path');
        const goo = document.createElementNS(SVG_NS, 'g');
        path.replaceWith(goo);
        goo.append(path);
        st.goo = goo;
        st.path = path;   /* ⚠️ referencia fija: ya no es el primer <path> de la caja (va detrás el de abajo) */
        st.dropLayer = goo.appendChild(document.createElementNS(SVG_NS, 'g'));
        st.drops = [];
        st.flip = zone.classList.contains('liquid-divider--flip');
        /* La onda de la navbar solo se estira HACIA ABAJO: hacia arriba el
           líquido se fundía con el fondo de la barra y solo se veía una línea
           rara (Guille, 30/09) */
        st.downOnly = !!zone.closest('.header__wave');
        /* En la volteada ese lado es transparente (deja ver el módulo de
           detrás): no hay color que prolongar, así que no lleva forma */
        if (!st.flip) {
          st.below = document.createElementNS(SVG_NS, 'path');
          st.below.setAttribute('class', 'liquid-divider__below');
          goo.before(st.below);
        }
        st.gooOffAt = 0;
        /* La onda se estira (preserveAspectRatio none): sin corregirlo, una
           gota redonda saldría ovalada. */
        /* uy: unidades del SVG por px de pantalla en vertical. La física se
           piensa en px (una gota salta lo mismo en la onda grande que en la
           pequeña) y se pasa a unidades con él. */
        const measure = () => {
          const r = svg.getBoundingClientRect();
          st.aspect = (r.width / W) / (r.height / 72 || 1);
          st.uy = 72 / (r.height || 72);
        };
        st.filterId = zone.dataset.dividerVariant === 'small' ? 'lny-goo-small' : 'lny-goo';
        measure();
        window.addEventListener('resize', measure);
      });
      const spawn = (st, x, dx, extra = {}) => {
        if (st.drops.length >= MAX_DROPS + (extra.burst ? 10 : 0)) return;
        const el = document.createElementNS(SVG_NS, 'ellipse');
        st.dropLayer.append(el);
        const edge = edgeAt(st, x);
        st.drops.push({
          el, x, y: edge - 4, r: 7 + Math.random() * 6,
          vx: dx * 0.03,
          /* Goteo: primero CUELGA del borde y se hincha (hang, en frames); luego
             se suelta despacio. Salpicadura: sale disparada hacia fuera. En px
             por frame; se pasan a unidades con st.uy. */
          hang: st.flip ? 0 : 28 + Math.random() * 24,
          vy: st.flip ? 3.2 + Math.random() * 2.2 : 0,
          ...extra,
        });
        if (!st.goo.hasAttribute('filter')) st.goo.setAttribute('filter', `url(#${st.filterId})`);
      };
      const stepDrops = (st, now) => {
        if (st.caught && !st.goo.hasAttribute('filter')) st.goo.setAttribute('filter', `url(#${st.filterId})`);
        if (!st.drops.length && !st.caught) {
          if (st.goo.hasAttribute('filter') && now > st.gooOffAt) st.goo.removeAttribute('filter');
          return;
        }
        st.gooOffAt = now + 0.4;   /* el filtro aguanta un poco tras la última gota */
        /* px/frame²; volteada = la gravedad la devuelve a su superficie */
        const g = st.flip ? -0.14 : 0.07;
        st.drops = st.drops.filter((d) => {
          if (d.hang > 0) {
            d.hang -= 1;
            d.y += 0.45 * st.uy;   /* cuelga y se estira: el filtro dibuja el hilo */
            d.r += 0.05;           /* y se hincha */
          } else {
            d.vy += g;
            d.y += d.vy * st.uy;
          }
          d.x += d.vx; d.vx *= 0.98;
          const edge = edgeAt(st, d.x);
          const below = (d.y - edge) / st.uy;   /* px bajo el borde */
          const gone = st.flip
            ? d.vy < 0 && d.y < edge - d.r * 0.3                     /* cayó de vuelta y se fundió */
            : (below > 40 && (d.r *= 0.985) < 1.5) || below > 220;   /* goteó y se consumió */
          if (gone) { d.el.remove(); return false; }
          d.el.setAttribute('cx', d.x.toFixed(1));
          d.el.setAttribute('cy', d.y.toFixed(1));
          d.el.setAttribute('rx', d.r.toFixed(2));
          d.el.setAttribute('ry', (d.r * st.aspect).toFixed(2));
          return true;
        });
      };

      let kick = 0;
      ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate(self) { kick = Math.min(18, Math.abs(self.getVelocity()) / 300); },
      });
      gsap.ticker.add((time) => {
        kick *= 0.93;
        loneDividers.forEach((zone, k) => {
          const st = states[k];
          const shape = SHAPES[zone.dataset.dividerVariant] || SHAPES.large;
          st.strength += (st.target - st.strength) * 0.07;
          step(st);
          const dir = k % 2 === 0 ? 1 : -1;
          st.path.setAttribute('d', wave(time * 1.4, kick, k * 2.1, dir, st, shape));
          stepDrops(st, time);
        });
      });

      /* ---------- Membrana ---------- */
      const REARM_S = 1.3;    /* s hasta que vuelve a estar lista: lo que dura el rebote. Antes 12 y se leía como «solo una vez» (Guille, 30/09: que se repita siempre) */
      const BREAK_PX = 140;   /* px de estiramiento antes de romperse (Guille: más estirada; antes 70) */
      const GRIP = 0.45;      /* cuánto sigue la película al cursor por frame. Con 0.1 la punta se quedaba por DETRÁS del puntero (Guille, 30/09): ahora va pegada a él, y la resistencia la pone la ruptura, no el retraso */
      const TIP_GAP = 10;     /* px de aire entre la punta y el puntero */
      let caught = null;
      const localY = (zone, st, clientY) => {
        const r = zone.getBoundingClientRect();
        return (st.flip ? (r.bottom - clientY) : (clientY - r.top)) / (r.height || 1) * 72;
      };
      const onDrag = (event) => {
        const { zone, st } = caught;
        const r = zone.getBoundingClientRect();
        st.px = Math.max(0, Math.min(W, ((event.clientX - r.left) / r.width) * W));
        st.cursorY = localY(zone, st, event.clientY);
      };
      const release = () => {
        const { st } = caught;
        caught = null;
        st.caught = false;
        window.removeEventListener('mousemove', onDrag);
        st.armedAt = gsap.ticker.time + REARM_S;
        /* Rebote: la película vuelve pasándose de largo, y la superficie
           entera se pone a ondular desde el punto de ruptura */
        const idx = st.px / (W / N);
        for (let i = 0; i <= N; i++) st.v[i] += Math.exp(-((i - idx) ** 2) / (2 * 4 ** 2)) * -st.pull * 0.35;
        /* Ráfaga de gotas desde la punta, hacia fuera del líquido */
        const tip = MID + st.pull * 0.8;
        for (let i = 0; i < 10; i++) {
          spawn(st, st.px + (Math.random() - 0.5) * 70, (Math.random() - 0.5) * 60, {
            burst: true,
            y: Math.max(tip, MID) - 4,
            r: 5 + Math.random() * 7,
            hang: st.flip ? 0 : Math.random() * 10,
            vy: st.flip ? 4 + Math.random() * 3 : 0.5 + Math.random() * 1.5,
          });
        }
        gsap.to(st, { pull: 0, duration: 1.2, ease: 'elastic.out(1, 0.35)' });
      };
      /* La película sigue al cursor con retraso; si se estira de más, se rompe.
         Red de seguridad: nunca retiene más de 1,5 s. */
      gsap.ticker.add((time) => {
        if (!caught) return;
        const { st } = caught;
        /* La punta se queda a TIP_GAP del puntero, sin llegar a taparlo. Se mide
           desde la superficie SIN la membrana en ese punto, no desde la línea
           media: la ola y el abultamiento del hover se suman a la punta, y
           medido desde MID la película se pasaba y el cursor quedaba debajo
           (Guille, 30/09) */
        const base = edgeAt(st, st.px) - st.pull;
        const raw = st.cursorY - base;
        st.pullTarget = raw - Math.sign(raw) * Math.min(Math.abs(raw), TIP_GAP * st.uy);
        /* En la volteada, hacia el líquido no se puede salir de la caja (ese
           lado es transparente y fuera no hay nada que recortar): tope */
        let target = st.flip ? Math.max(st.pullTarget, -(MID - 6)) : st.pullTarget;
        if (st.downOnly) target = Math.max(0, target);
        st.pull += (target - st.pull) * GRIP;
        /* Se rompe cuando el RATÓN se ha alejado lo bastante. En la volteada, el
         estiramiento hacia el líquido está topado (arriba), así que medirlo por
         la película no llegaba nunca al umbral y la membrana no se rompía:
         ninguna onda volteada tenía el efecto (Guille, 30/09) */
      const reach = target !== st.pullTarget ? Math.abs(st.pullTarget) : Math.abs(st.pull);
      if (reach / st.uy > BREAK_PX || time - st.caughtAt > 1.5) release();
      });

      loneDividers.forEach((zone, k) => {
        const st = states[k];
        st.pull = 0; st.px = W / 2; st.lastD = null; st.armedAt = 0;
        zone.addEventListener('mousemove', (event) => {
          /* ¿El cursor acaba de CRUZAR el borde? La primera vez, la película se le pega */
          if (!caught && gsap.ticker.time >= st.armedAt) {
            const rect0 = zone.getBoundingClientRect();
            const x0 = ((event.clientX - rect0.left) / rect0.width) * W;
            const d = localY(zone, st, event.clientY) - edgeAt(st, x0);
            /* La de la navbar solo engancha al cruzarla hacia abajo */
            if (st.lastD !== null && Math.sign(d) !== Math.sign(st.lastD) && !(st.downOnly && d < 0)) {
              caught = { zone, st };
              st.caught = true;
              st.caughtAt = gsap.ticker.time;
              st.px = x0;
              st.pullTarget = d;
              st.cursorY = localY(zone, st, event.clientY);
              window.addEventListener('mousemove', onDrag);
            }
            st.lastD = d;
          }
          const rect = zone.getBoundingClientRect();
          const x = ((event.clientX - rect.left) / rect.width) * W;
          st.target = 1;
          if (st.prevX !== null) {
            const push = Math.min(Math.abs(x - st.prevX), 60) * 0.09;
            const idx = x / (W / N);
            for (let i = 0; i <= N; i++) {
              st.v[i] += Math.exp(-((i - idx) ** 2) / (2 * 1.6 ** 2)) * push;
            }
            /* Cuanto más rápido pasa el ratón, más gotas suelta */
            const speed = Math.abs(x - st.prevX);
            if (speed > 3 && Math.random() < Math.min(0.6, speed / 40)) spawn(st, x, x - st.prevX);
          }
          st.prevX = x;
          st.mx = x;
        });
        /* Chapoteo a demanda: quien quiera que la onda llegue «temblando» (la
           de la cabecera al volver al top) lanza liquid:splash sobre ella */
        zone.addEventListener('liquid:splash', () => {
          for (let i = 0; i <= N; i++) st.v[i] += Math.sin(i * 0.9) * 5 + (i % 2 ? 2.5 : -2.5);
        });
        zone.addEventListener('mouseleave', () => {
          st.target = 0;
          st.prevX = null;
          st.lastD = null;
        });
      });
    }

    /* Manifiesto: slider de pasos con la sección pineada. Un gesto = un paso
       entero (snap direccional, sin coordenadas intermedias); cada paso carga
       su frase palabra a palabra, dibuja sus marcas de rotulador y trae su
       foto con un barrido dentro del marco. La foto lleva además el parallax
       interno del sistema (overscan + deslizamiento a otra velocidad). */
    document.querySelectorAll('[data-motion="manifesto"]').forEach((root) => {
      const steps = [...root.querySelectorAll('.manifesto__step')];
      const slides = [...root.querySelectorAll('.manifesto__slide')];
      const dots = [...root.querySelectorAll('[data-manifesto-dot]')];
      const prevBtn = root.querySelector('[data-manifesto-prev]');
      const nextBtn = root.querySelector('[data-manifesto-next]');
      /* Las dos etiquetas del boton derecho vienen de los locales, no del JS:
         en el ultimo paso deja de ser "siguiente" y pasa a ser "reiniciar" */
      const labelNext = nextBtn ? nextBtn.getAttribute('aria-label') : '';
      const labelRestart = nextBtn ? nextBtn.dataset.restartLabel || labelNext : '';
      const label = root.querySelector('.manifesto__label');
      const bend = root.querySelector('[data-manifesto-bend]');
      const images = [...root.querySelectorAll('.manifesto__slide image')];
      if (steps.length < 2) return;

      const SLIDE_W = 755; // ancho del viewBox: un barrido completo
      const PARALLAX = 260; // recorrido del deslizamiento interno de la foto
      const last = steps.length - 1;
      const inc = 1 / last;

      /* Marcas sin dibujar de salida. El estado de partida vive aqui y no en
         components.css a proposito: sin JS o con motion reducido las marcas
         tienen que verse trazadas, no quedarse invisibles para siempre. */
      gsap.set(root.querySelectorAll('.mk-strike svg'), {
        clipPath: 'inset(-40% 100% -40% -5%)',
      });
      gsap.set(root.querySelectorAll('.mk-circle svg'), { '--sweep': '0deg' });

      const setParallax = (y) => gsap.set(images, { attr: { y: y } });
      setParallax(-PARALLAX);
      slides.forEach((slide, i) => gsap.set(slide, { x: i === 0 ? 0 : SLIDE_W }));

      /* Cada palabra en su propio span: el texto se carga en cascada.

         Lo que parte una palabra es el ESPACIO, no el nodo del DOM. El copy
         marca trozos con [corchetes] e *itálicas*, asi que una frase llega
         partida en varios nodos y la puntuacion que sigue a un marcado cae en
         el nodo siguiente: "entre el <em>con</em>, se quedan" deja la coma
         pegada al inicio del texto. Si cada nodo abriera su propio .word, esa
         coma seria una palabra suelta — con su hueco delante y su propio turno
         en la cascada, y pudiendo caer sola al principio de una linea.

         Por eso se arrastra `lastWord`: mientras no haya pasado un espacio,
         todo lo que venga se mete DENTRO del mismo span. El espacio es lo
         unico que lo cierra. */
      steps.forEach((step) => {
        const phrase = step.querySelector('.manifesto__phrase');
        if (!phrase) return;
        const parts = [];
        let lastWord = null;

        const openWord = (content) => {
          const span = document.createElement('span');
          span.className = 'word';
          span.append(content);
          parts.push(span);
          lastWord = span;
        };

        [...phrase.childNodes].forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            node.textContent.split(/(\s+)/).forEach((token) => {
              if (!token) return;
              if (/^\s+$/.test(token)) {
                parts.push(document.createTextNode(' '));
                lastWord = null;
                return;
              }
              if (lastWord) lastWord.append(token);
              else openWord(token);
            });
          } else if (lastWord) {
            lastWord.append(node);
          } else {
            openWord(node);
          }
        });
        phrase.replaceChildren(...parts);
      });

      /* Barrido de foto: la entrante empuja a la saliente */
      let currentSlide = 0;
      let sweeping = null;
      const slidesWrap = root.querySelector('[data-manifesto-slides]');
      const sweepTo = (index) => {
        if (index === currentSlide) return;
        const dir = index > currentSlide ? 1 : -1;
        const incoming = slides[index];
        const outgoing = slides[currentSlide];
        currentSlide = index;
        slidesWrap.appendChild(incoming);
        if (sweeping) sweeping.kill();
        sweeping = gsap
          .timeline({ defaults: { duration: 0.8, ease: 'power3.inOut' } })
          .fromTo(incoming, { x: dir * SLIDE_W }, { x: 0 }, 0)
          .fromTo(outgoing, { x: 0 }, { x: -dir * SLIDE_W * 0.35 }, 0);
      };

      /* En el ultimo paso el boton "siguiente" NO se apaga: se convierte en
         reinicio y vuelve al paso 1. Un stepper con autoplay que se queda sin
         salida obliga a scrollear para volver a verlo, y el modulo es
         justamente el que cuenta la marca. El intercambio de icono lo hace el
         CSS leyendo data-at-end; aqui solo se marca el estado y se cambia la
         etiqueta, que es lo que oye un lector de pantalla. */
      const syncNav = (index) => {
        dots.forEach((dot, i) => dot.setAttribute('aria-selected', String(i === index)));
        if (prevBtn) prevBtn.disabled = index <= 0;
        if (nextBtn) {
          const atEnd = index >= last;
          nextBtn.toggleAttribute('data-at-end', atEnd);
          nextBtn.setAttribute('aria-label', atEnd ? labelRestart : labelNext);
        }
      };

      let currentStep = -1;
      let stepTl = null;
      const showStep = (index, dir) => {
        if (index === currentStep) return;
        const prev = currentStep;
        currentStep = index;
        syncNav(index);
        sweepTo(index);
        if (stepTl) stepTl.kill();
        steps.forEach((step, i) => {
          if (i !== index && i !== prev) gsap.set(step, { autoAlpha: 0, visibility: 'hidden' });
        });

        const step = steps[index];
        const words = step.querySelectorAll('.word');
        const strikes = step.querySelectorAll('.mk-strike svg');
        const circle = step.querySelector('.mk-circle svg');
        const cta = step.querySelector('.manifesto__cta');

        stepTl = gsap.timeline();
        if (prev >= 0) {
          stepTl
            .to(steps[prev], { autoAlpha: 0, y: -26 * dir, duration: 0.3, ease: 'power2.in' }, 0)
            .set(steps[prev], { visibility: 'hidden' });
        }
        stepTl
          .set(step, { visibility: 'visible', autoAlpha: 1, y: 0 }, prev >= 0 ? 0.18 : 0)
          .set(strikes, { clipPath: 'inset(-40% 100% -40% -5%)' }, '<')
          .set(circle, { '--sweep': '0deg' }, '<')
          .fromTo(
            words,
            { y: 34 * dir, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.55, ease: 'power3.out', stagger: 0.055 },
            '<'
          );
        if (strikes.length) {
          stepTl.to(
            strikes,
            { clipPath: 'inset(-40% -5% -40% -5%)', duration: 0.45, ease: 'power2.out', stagger: 0.5 },
            '>+0.3'
          );
        }
        if (circle) {
          stepTl.to(circle, { '--sweep': '360deg', duration: 1, ease: 'power2.inOut' }, '>+0.4');
        }
        if (cta) {
          /* Cierra el paso: arranca cuando el trazo anterior ha terminado,
             sin encabalgarse con él (regla 5 del manual). */
          stepTl.fromTo(
            cta,
            { y: 24, autoAlpha: 0 },
            { y: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out' },
            '>'
          );
        }
      };

      /* Entrada del módulo: la forma se pega y el label cae como pegatina */
      gsap.from(root.querySelector('.manifesto__shape'), {
        scale: 1.35,
        rotation: -6,
        autoAlpha: 0,
        transformOrigin: '50% 50%',
        duration: 0.65,
        ease: 'back.out(1.8)',
        scrollTrigger: { trigger: root, start: enterAt('top 72%'), toggleActions: 'play none none reverse' },
      });

      if (label) {
        // el estado final sale del token, no de un valor repetido aqui
        const restShadow = getComputedStyle(document.documentElement)
          .getPropertyValue('--shadow-sticker').trim() || 'drop-shadow(3px 4px 0 rgba(86, 25, 43, 0.28))';
        const labelTl = gsap.timeline({
          scrollTrigger: { trigger: root, start: enterAt('top 58%'), toggleActions: 'play none none reverse' },
        });
        if (bend) labelTl.set(bend, { attr: { scale: 42 } });
        labelTl
          .fromTo(
            label,
            { scale: 2.2, rotation: 18, autoAlpha: 0, filter: 'drop-shadow(0px 30px 26px rgba(86, 25, 43, 0.45))' },
            {
              scale: 1.06,
              rotation: 4,
              autoAlpha: 1,
              filter: 'drop-shadow(0px 12px 10px rgba(86, 25, 43, 0.38))',
              duration: 0.36,
              ease: 'power2.in',
            }
          )
          .to(label, {
            scale: 1,
            rotation: 6,
            filter: restShadow,
            duration: 0.34,
            ease: 'back.out(4)',
          });
        if (bend) labelTl.to(bend, { attr: { scale: 0 }, duration: 1.1, ease: 'elastic.out(1, 0.35)' }, '<-0.05');
      }

      /* Autoplay de cortesía: si el usuario se detiene, el slider da UNA
         vuelta completa y se apaga. Cualquier gesto dentro lo cancela. */
      const autoDelay = parseFloat(root.dataset.autoplay) * 1000;
      const hasAutoplay = Number.isFinite(autoDelay) && autoDelay > 0;
      let autoDone = !hasAutoplay;
      let autoTimer = null;
      let scrollTween = null;

      const pinST = ScrollTrigger.create({
        trigger: root,
        start: 'top top',
        end: `+=${last * 120}%`,
        pin: true,
        anticipatePin: 1,
        onToggle(self) {
          if (self.isActive) scheduleAutoplay();
        },
        onUpdate(self) {
          const index = Math.round(self.progress / inc);
          if (index !== currentStep) {
            showStep(index, index > (currentStep < 0 ? -1 : currentStep) ? 1 : -1);
          }
          // sin deslizamiento vertical entre pasos: dentro del slider solo
          // hay barrido horizontal; el vertical queda para el scroll real
        },
        snap: {
          snapTo(value, self) {
            // zona muerta: un roce vuelve al paso actual, un gesto real avanza
            const nearest = Math.round(value / inc) * inc;
            if (Math.abs(value - nearest) < 90 / (self.end - self.start)) return nearest;
            return self.direction > 0
              ? Math.min(1, Math.ceil(value / inc) * inc)
              : Math.max(0, Math.floor(value / inc) * inc);
          },
          duration: { min: 0.5, max: 0.85 },
          delay: 0.05,
          ease: 'power2.inOut',
        },
      });

      /* El primer paso carga al asomar el módulo: la columna nunca llega vacía */
      ScrollTrigger.create({
        trigger: root,
        start: enterAt('top 45%'),
        onEnter() {
          if (currentStep < 0) showStep(0, 1);
          scheduleAutoplay();
        },
        onLeaveBack() {
          currentStep = -1;
          if (stepTl) stepTl.kill();
          clearTimeout(autoTimer);
          steps.forEach((step) => gsap.set(step, { autoAlpha: 0, visibility: 'hidden' }));
        },
      });

      /* Parallax interno también con el scroll manual de entrada y salida */
      ScrollTrigger.create({
        start: () => pinST.start - window.innerHeight,
        end: () => pinST.start,
        onUpdate(self) {
          if (pinST.progress === 0) setParallax(-PARALLAX + PARALLAX * 0.5 * self.progress);
        },
      });
      ScrollTrigger.create({
        start: () => pinST.end,
        end: () => pinST.end + window.innerHeight,
        onUpdate(self) {
          if (pinST.progress === 1) setParallax(-PARALLAX * 0.5 + PARALLAX * 0.5 * self.progress);
        },
      });

      /* Viaje a un paso concreto: lo mueve un tween (continuo desde el primer
         frame) — el scroll suave nativo arranca tan lento que el snap lo
         confundía con quietud y lo abortaba. */
      const goToStep = (index) => {
        const target = pinST.start + gsap.utils.clamp(0, 1, index * inc) * (pinST.end - pinST.start);
        if (scrollTween) scrollTween.kill();
        const pos = { y: window.scrollY };
        scrollTween = gsap.to(pos, {
          y: target,
          duration: 0.7,
          ease: 'power2.inOut',
          onUpdate: () => window.scrollTo(0, pos.y),
        });
      };

      const inSlider = () => window.scrollY >= pinST.start - 1 && window.scrollY <= pinST.end + 1;

      function scheduleAutoplay() {
        if (autoDone) return;
        clearTimeout(autoTimer);
        autoTimer = setTimeout(() => {
          if (autoDone || currentStep < 0 || !inSlider()) return;
          if (currentStep < last) {
            goToStep(currentStep + 1);
            scheduleAutoplay();
          } else {
            /* ⚠️ Al llegar al ultimo paso el autoplay SE APAGA Y SE QUEDA AHI.
               Antes volvia al primero, y eso dejaba al visitante atrapado: el
               modulo esta pineado, asi que devolverlo al paso 1 lo mandaba al
               principio del pin justo cuando queria seguir bajando — y como el
               tween de scroll pelea con su gesto, se leia como que el slider se
               atascaba en el tercer paso. Quien quiera repetir tiene los puntos
               y la flecha de reinicio. */
            autoDone = true;
          }
        }, autoDelay);
      }

      const onUserGesture = () => {
        /* El tween de scroll muere con el primer gesto pase lo que pase: dos
           cosas moviendo el scroll a la vez es lo que se siente como un tiron. */
        if (scrollTween) {
          scrollTween.kill();
          scrollTween = null;
        }
        if (autoDone) return;
        if (inSlider()) {
          autoDone = true;
          clearTimeout(autoTimer);
        } else if (currentStep >= 0) {
          scheduleAutoplay();
        }
      };
      window.addEventListener('wheel', onUserGesture, { passive: true });
      window.addEventListener('touchmove', onUserGesture, { passive: true });

      const takeControl = () => {
        autoDone = true;
        clearTimeout(autoTimer);
      };
      dots.forEach((dot, i) =>
        dot.addEventListener('click', () => {
          takeControl();
          goToStep(i);
        })
      );
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          takeControl();
          goToStep(currentStep - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          takeControl();
          /* En el ultimo paso vuelve al primero en vez de no hacer nada */
          goToStep(currentStep >= last ? 0 : currentStep + 1);
        });
      }
      syncNav(0);
    });

    /* Navbar: pasado el hero, BAJAR LA ESCONDE. Vuelve en dos casos, que son los
       dos en los que el usuario esta buscando algo y no leyendo:
       - **sube**: se ha pasado algo y quiere los enlaces;
       - **se detiene 2 s**: ha dejado de recorrer y esta decidiendo.
       Nunca se esconde con el foco o el raton dentro (accesibilidad), ni en el
       hero, ni arriba del todo.

       El header-group son VARIAS secciones sticky apiladas (faldon de envio +
       navbar), y cada una lleva la clase del grupo: se esconden todas juntas o
       la de abajo sube a ocupar el hueco de la que se fue. */
    /* La onda que remata la navbar solo tiene sentido con la barra EN SU SITIO:
       en cuanto la pagina arranca, la barra pasa a flotar sobre el contenido y
       una onda ahi ya no remata nada. Se marca en la raiz y lo demas es CSS.
       ⚠️ Fuera del `if` de las cabeceras: esta clase la pinta el CSS del header
       pero no depende de que exista el grupo sticky. */
    /* Al volver arriba la onda cae de la barra con su rebote (CSS) y se queda
       quieta: el chapoteo al llegar se probó y se quitó (Guille, 30/09: el
       temblor al asentarse sobraba) */
    const marcarScroll = () =>
      document.documentElement.classList.toggle('page-is-scrolled', window.scrollY > 0);
    marcarScroll();
    window.addEventListener('scroll', marcarScroll, { passive: true });

    const headerWraps = [...document.querySelectorAll('.shopify-section-group-header-group')];
    if (headerWraps.length) {
      let idleTimer;
      let lastY = window.scrollY;
      const IDLE = token('--motion-navbar-idle', 2000);
      const engaged = () =>
        headerWraps.some(
          (wrap) => wrap.matches(':hover') || wrap.contains(document.activeElement)
        );
      const setHidden = (on) =>
        headerWraps.forEach((wrap) => wrap.classList.toggle('is-hidden', on && !engaged()));

      window.addEventListener(
        'scroll',
        () => {
          const y = window.scrollY;
          /* El rebote elastico de iOS da deltas de 1-2 px con el dedo quieto:
             sin umbral, cualquier temblor se leeria como "esta subiendo". */
          const subiendo = y < lastY - 2;
          const bajando = y > lastY + 2;
          if (subiendo || bajando) lastY = y;

          /* ⚠️ El temporizador solo se reinicia si el scroll SE MOVIO. Con
             `clearTimeout` en cada evento no volvia nunca: los ScrollTrigger
             con scrub y los modulos pineados siguen emitiendo `scroll` aunque
             el visitante este quieto, asi que la cuenta atras se reiniciaba
             sola para siempre. */
          if (!subiendo && !bajando) return;

          clearTimeout(idleTimer);
          /* ⚠️ SOLO dos cosas la traen de vuelta: subir o pararse. Antes se
             mostraba tambien durante todo el hero (`pastHero()`), asi que en la
             home no se escondia hasta pasado el modulo entero y parecia que la
             regla no se aplicaba. Ahora el hero no es una excepcion.
             `y <= 0` si lo es, y no es lo mismo: arriba del todo no ha habido
             ningun gesto todavia — la pagina no puede abrir sin cabecera. */
          /* ⚠️ Tampoco mientras su HUECO siga en pantalla: el grupo es sticky y
             ocupa sitio arriba del todo hasta que el scroll lo pasa; si se
             esconde antes, ese hueco enseña el fondo blanco de la pagina
             (Guille, 30/09). Se esconde cuando lo de debajo ya llena el alto. */
          const slot = headerWraps.reduce((h, wrap) => h + wrap.offsetHeight, 0);
          /* Mientras la cabecera de la home está fijada, la navbar se queda:
             el wordmark aterriza en ella (hero-home pone la clase) */
          const hold = document.documentElement.classList.contains('is-hero-hold');
          if (y <= slot || subiendo || hold) {
            setHidden(false);
          } else if (bajando) {
            setHidden(true);
          }
          /* Se ha parado: vuelve. El temporizador se reinicia en cada scroll,
             asi que solo dispara cuando de verdad deja de moverse. */
          idleTimer = setTimeout(() => setHidden(false), IDLE);
        },
        { passive: true }
      );
    }

    /* El wordmark del hero vuela hasta el hueco de la navbar (ref. Wonder).
       Scrub sobre los primeros px de scroll; al aterrizar hay relevo de
       opacidad con el logo real de la navbar, reversible al subir. */
    const mark = document.querySelector('[data-motion="hero-logo"] img');
    const slot = document.querySelector('.header__logo');
    const heroSection = document.querySelector('.hero-brand');
    if (mark && slot && heroSection) {
      const slotMark = slot.querySelector('img') || slot;
      gsap.set(slot, { autoAlpha: 0 });

      /* Distancia de vuelo: ideal = un alto de wordmark y pico, pero capada
         al scroll que la página tenga de verdad (una home a medio construir
         puede ser mas corta que el vuelo, y el relevo no llegaria nunca). */
      const flight = () => {
        const heroTop = heroSection.getBoundingClientRect().top + window.scrollY;
        const room = ScrollTrigger.maxScroll(window) - heroTop - 8;
        const ideal = mark.offsetHeight + token('--motion-reveal-distance', 24) * 4;
        return Math.max(60, Math.min(ideal, room));
      };

      gsap.fromTo(
        mark,
        { x: 0, y: 0, scale: 1 },
        {
          x: () => slotMark.getBoundingClientRect().left - mark.getBoundingClientRect().left,
          y: () =>
            slotMark.getBoundingClientRect().top -
            mark.getBoundingClientRect().top +
            flight(),
          scale: () => slotMark.offsetHeight / mark.offsetHeight,
          transformOrigin: 'left top',
          ease: FLIGHT_EASE,
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: () => `+=${flight()}`,
            scrub: LAG,
            invalidateOnRefresh: true,
            onUpdate(self) {
              const landed = self.progress > 0.96;
              gsap.set(slot, { autoAlpha: landed ? 1 : 0 });
              gsap.set(mark, { autoAlpha: landed ? 0 : 1 });
            },
          },
        }
      );
    }
    /* B Corp: el estandarte cae plegado y se despliega con su peso, y los
       compromisos entran con su marca de rotulador. Cierra el CTA, como manda
       la regla del orden de aparición. */
    document.querySelectorAll('[data-motion="bcorp"]').forEach((root) => {
      const banner = root.querySelector('.bcorp__banner');
      const flag = root.querySelector('.bcorp__flag');
      const seal = root.querySelector('.bcorp__seal');
      const folds = root.querySelector('.bcorp__folds');
      if (!banner) return;

      /* La cabecera es el componente del sistema (section-heading) y viene sin
         data-motion a proposito: la entra este timeline, sincronizada con el
         estandarte, en vez del batch global de [data-motion="reveal"]. */
      /* Si la cabecera lleva la entrada de títulos del sistema
         (title-section), la anima esa y aquí no se toca: dos tweens sobre las
         mismas piezas se pisarían */
      const intro = root.querySelector('[data-motion="title-section"]')
        ? []
        : root.querySelectorAll('.section-heading__eyebrow, .section-heading__title, .section-heading__subtitle');
      const commitments = root.querySelectorAll('.bcorp__commitment');
      const marks = root.querySelectorAll('.bcorp__commitment .mk svg');
      const cta = root.querySelector('.bcorp__cta');

      /* Estado de partida: el fardo plegado y los trazos sin dibujar. Va aqui
         y no en el CSS para que sin JS (o con motion reducido) el estandarte
         se vea desplegado, con su sello y con las marcas ya trazadas. */
      gsap.set(banner, { scaleY: 0.05 });
      gsap.set(seal, { autoAlpha: 0 });
      gsap.set(folds, { autoAlpha: 0.9 });
      gsap.set(marks, { clipPath: 'inset(-40% 100% -40% -5%)' });

      const tl = gsap.timeline({
        scrollTrigger: { trigger: root, start: enterAt('top 70%'), once: true },
      });

      tl
        /* 1 · se suelta: cae acelerando, la tela pesa */
        .to(banner, { scaleY: 1.04, duration: 0.55, ease: 'power2.in' })
        /* 2 · frenazo al final del pano y rebote amortiguado */
        .to(banner, { scaleY: 0.955, duration: 0.13, ease: 'power2.out' })
        .to(banner, { scaleY: 1, duration: 1.1, ease: 'elastic.out(1, 0.32)' })
        /* 3 · los pliegues se alisan y, ya sin ellos, aparece el sello */
        .to(folds, { autoAlpha: 0, duration: 0.5, ease: 'power2.out' }, '-=1.05')
        .to(seal, { autoAlpha: 1, duration: 0.45, ease: 'power2.out' }, '-=0.62')
        /* 4 · la sombra se asienta y el pano oscila hasta pararse */
        .to(
          flag,
          {
            filter: 'drop-shadow(0 10px 16px rgb(86 25 43 / 22%))',
            duration: 0.5,
            ease: 'power2.out',
          },
          '-=0.9'
        )
        .fromTo(
          banner,
          { rotate: -2.4 },
          { rotate: 0, duration: 1.6, ease: 'elastic.out(1, 0.28)' },
          '-=1.05'
        );
      if (intro.length) {
        tl.from(intro, { y: 18, autoAlpha: 0, duration: 0.5, stagger: 0.09, ease: 'power2.out' }, '-=1.5');
      }

      if (commitments.length) {
        tl.from(
          commitments,
          { y: 10, autoAlpha: 0, duration: 0.4, stagger: 0.16, ease: 'power2.out' },
          '-=0.5'
        );
      }
      if (marks.length) {
        /* El trazo se dibuja con la tela ya estirada, uno detras de otro */
        tl.to(
          marks,
          { clipPath: 'inset(-40% -5% -40% -5%)', duration: 0.42, stagger: 0.16, ease: 'power2.out' },
          '-=0.55'
        );
      }
      if (cta) {
        tl.from(cta, { y: 14, autoAlpha: 0, duration: 0.45, ease: 'power2.out' });
      }
    });

    /* Los frutos secos del hero. Cada uno viaja a SU velocidad y en sentidos
       opuestos: lo que se lee como profundidad es que se separen entre si y de
       la foto, no que se muevan. */
    document.querySelectorAll('[data-motion="hero-parallax"]').forEach((capa) => {
      const piezas = capa.querySelectorAll('[data-parallax-speed]');
      if (!piezas.length) return;
      const trigger = capa.closest('.hero-brand') || capa;
      piezas.forEach((pieza) => {
        const speed = parseFloat(pieza.dataset.parallaxSpeed) || 0;
        gsap.to(pieza, {
          /* ⚠️ `y` en pixeles y no `yPercent`: los dos frutos miden distinto y
             con porcentajes el pequeno recorreria menos camino, asi que la
             pareja se descuadraria en vez de moverse a la vez. */
          y: () => speed * window.innerHeight * 0.45,
          ease: 'none',
          scrollTrigger: {
            trigger,
            start: 'top top',
            end: 'bottom top',
            scrub: LAG,
            invalidateOnRefresh: true,
          },
        });
      });
    });

    /* For Business: el mosaico que hace zoom. Las de alrededor se apartan y se
       van —el foco se gana quitando, no solo agrandando— y la central crece
       hasta cubrir la pantalla, donde se queda como cabecera a sangre. */
    document.querySelectorAll('[data-motion="business-zoom"]').forEach((modulo) => {
      const grid = modulo.querySelector('[data-zoom-grid]');
      const hero = modulo.querySelector('[data-zoom-hero]');
      const onda = modulo.querySelector('[data-zoom-wave]');
      if (!grid || !hero) return;
      const resto = [...grid.children].filter((celda) => celda !== hero);

      /* Cuanto tiene que crecer para llenar el viewport. Se MIDE en cada
         refresh, no se estima: depende del tamano real del mosaico en cada
         pantalla, y con `vh` en las filas cambia con la ventana. */
      const escala = () => {
        const c = hero.getBoundingClientRect();
        if (!c.width || !c.height) return 1;
        return Math.max(window.innerWidth / c.width, window.innerHeight / c.height);
      };

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: modulo.querySelector('.business-zoom__track') || modulo,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 0.45,
          invalidateOnRefresh: true,
        },
      });

      /* Cada una huye hacia SU lado, asi que el grupo se abre en vez de
         encogerse hacia el centro. */
      tl.to(resto, {
        scale: 0.72,
        autoAlpha: 0,
        x: (i, el) => (el.getBoundingClientRect().left - hero.getBoundingClientRect().left) * 0.6,
        y: (i, el) => (el.getBoundingClientRect().top - hero.getBoundingClientRect().top) * 0.6,
        ease: 'none',
        duration: 0.35,
      }, 0);

      tl.to(hero, { scale: escala, ease: 'none', duration: 1 }, 0);

      /* La onda entra cuando la foto YA manda: antes se leeria como un adorno
         suelto sobre el mosaico, no como el borde de la imagen. */
      if (onda) tl.to(onda, { opacity: 1, ease: 'none', duration: 0.22 }, 0.55);
    });

    /* team-carousel: en móvil el equipo es un carrusel HORIZONTAL ligado al
       scroll (Guille, 30/09)
       Al llegar al módulo, se queda fijo bajo la cabecera (pin) y el scroll
       vertical mueve la fila de tarjetas de derecha a izquierda; cuando ha
       pasado la última, la página sigue bajando. El recorrido vertical es el
       mismo que el horizontal, así que el gesto se siente 1:1.
       La timeline horizontal se deja en grid.lnyHorizontal: flower-open la usa
       (containerAnimation) para abrir cada flor al entrar por la derecha.
       ⚠️ Va justo DETRÁS del hero en el orden de los <script>: es un pin, y
       todo lo que va debajo en la página tiene que crearse después.
       En escritorio: la rejilla de 4 × 2 de siempre. Sin motion: la fila se
       desliza con el dedo (scroll horizontal nativo con snap, en el CSS). */
    (() => {
      if (!matchMedia('(max-width: 749px)').matches) return;
      document.querySelectorAll('[data-team-carousel]').forEach((section) => {
        const grid = section.querySelector('.brand-team__grid');
        if (!grid) return;
        grid.classList.add('is-scroll-carousel');
        const headerH = () => document.querySelector('.site-header')?.offsetHeight || 0;
        /* Lo que sobra a la derecha: el ancho de la fila menos el de la pantalla */
        const distance = () => Math.max(0, grid.scrollWidth - document.documentElement.clientWidth);
        grid.lnyHorizontal = gsap.to(grid, {
          x: () => -distance(),
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: () => `top ${headerH()}px`,
            end: () => `+=${distance()}`,
            pin: true,
            scrub: LAG,
            invalidateOnRefresh: true,
          },
        });
      });
    })();

    /* flower-open: las flores del equipo se ABREN al entrar (PRUEBA, Guille 29/09)
       Referencia: demos.gsap.com/demo/canvas-morphs (MorphSVGPlugin). Cada flor
       nace como un círculo pequeño en el centro y se transforma en la flor
       mientras crece, destapando la foto. Recorte, sombra granate y filo se
       transforman a la vez.
       Sin canvas a propósito: la demo pinta en canvas para mover decenas de
       formas; con 8 no hace falta, y en canvas habría que redibujar la foto a
       mano y se perdería el hover de CSS (la flor que crece y gira).
       Las 8 flores compartían UN recorte (#lny-flower): para que cada una se abra
       a su tiempo, cada tarjeta recibe su copia (#lny-flower-0…7) y su filo pasa
       de <use> a un <path> propio.
       HOVER (Guille, 29/09): la flor se transforma en un RECTÁNGULO con esquinas
       redondeadas que ocupa toda la caja de la foto, así que descubre más imagen;
       al salir, vuelve a flor. Mientras hay morph, el giro de 15° del hover del
       theme se apaga (un rectángulo girado se lee torcido): quedan el crecimiento
       de la figura y el alejamiento de la foto.
       Sin motion o sin MorphSVG: las flores, enteras desde el principio. */
    (() => {
      if (!window.MorphSVGPlugin) return;
      const source = document.getElementById('lny-flower-path');
      const clip = document.getElementById('lny-flower');
      if (!source || !clip) return;
      const SVG_NS = 'http://www.w3.org/2000/svg';
      const FLOWER = source.getAttribute('d');
      /* Semilla: círculo de radio 30 en el centro de la caja de la flor (254 × 244) */
      const SEED = 'M97.2,121.8a30,30 0 1,0 60,0a30,30 0 1,0 -60,0';
      const OPEN = { duration: 1.3, ease: 'power3.inOut' };
      /* Rectángulo de la caja entera de la flor (254,416 × 243,544) con el radio
         del token --radius-lg; el recorte usa esas mismas unidades. */
      const R = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--radius-lg')) * 16 || 24;
      const W = 254.416, H = 243.544;
      const RECT = `M${R},0 H${W - R} A${R},${R} 0 0 1 ${W},${R} V${H - R} A${R},${R} 0 0 1 ${W - R},${H} `
        + `H${R} A${R},${R} 0 0 1 0,${H - R} V${R} A${R},${R} 0 0 1 ${R},0 Z`;
      const HOVER = { duration: 0.6, ease: 'power3.out', overwrite: true };

      const grid = document.querySelector('[data-team-morph] .brand-team__grid');
      grid?.classList.add('has-flower-morph');
      if (!grid) return;
      const cards = [...grid.querySelectorAll('.team-member')];
      const shapes = new Map();
      cards.forEach((card, i) => {
        const id = `lny-flower-${i}`;
        const ownClip = clip.cloneNode(false);   /* mismas unidades y escala */
        ownClip.id = id;
        const clipPath = document.createElementNS(SVG_NS, 'path');
        clipPath.setAttribute('d', SEED);
        ownClip.append(clipPath);
        clip.after(ownClip);
        card.querySelectorAll('.team-member__shape, .team-member__photo')
          .forEach((el) => { el.style.clipPath = `url(#${id})`; });

        const use = card.querySelector('.team-member__outline use');
        const outline = document.createElementNS(SVG_NS, 'path');
        outline.setAttribute('d', SEED);
        use?.replaceWith(outline);
        shapes.set(card, [clipPath, outline]);
      });

      const open = (card, delay = 0) => {
        const paths = shapes.get(card);
        if (!paths || card.dataset.flowerOpen !== undefined) return;
        card.dataset.flowerOpen = '';
        gsap.to(paths, { morphSVG: FLOWER, ...OPEN, delay });
      };
      /* Hover y foco de teclado: flor ↔ rectángulo. Si aún no se había abierto,
         se abre ya (el hover no espera a la cascada). */
      const hover = (card, on) => {
        const paths = shapes.get(card);
        if (card.dataset.flowerOpen === undefined) card.dataset.flowerOpen = '';
        gsap.to(paths, { morphSVG: on ? RECT : FLOWER, ...HOVER });
      };
      cards.forEach((card) => {
        card.addEventListener('mouseenter', () => hover(card, true));
        card.addEventListener('mouseleave', () => hover(card, false));
        card.addEventListener('focusin', () => hover(card, true));
        card.addEventListener('focusout', (e) => { if (!card.contains(e.relatedTarget)) hover(card, false); });
      });
      /* Por filas: las que entran juntas se abren en cascada */
      /* En el carrusel horizontal de móvil (team-carousel) las tarjetas no
         entran por abajo sino por la DERECHA: cada flor se abre al entrar ella */
      const horizontal = grid?.lnyHorizontal;
      if (horizontal) {
        /* ⚠️ Sin ScrollTriggers con containerAnimation: dentro de la fila
           fijada salían con inicio y fin 0 y se disparaban todos a la vez. Se
           mira directamente dónde está cada tarjeta en cada fotograma del
           desplazamiento (onUpdate de la timeline horizontal, que ya lleva la
           inercia del scrub, así que va a la par de lo que se ve). */
        const check = () => {
          const vw = document.documentElement.clientWidth;
          cards.forEach((card) => {
            const r = card.getBoundingClientRect();
            /* se abre al entrar por la derecha */
            if (r.left < vw * 0.85 && r.right > 0) open(card);
            /* Al CRUZAR el centro de la pantalla se vuelve cuadrado —el gesto
               del hover de escritorio: se endereza y crece— y SE QUEDA así
               mientras se sigue bajando; solo vuelve a flor si se sube y cruza
               el centro en sentido contrario (Guille, 30/09). */
            const passed = r.left + r.width / 2 <= vw / 2 + 2;   /* 2 px de tolerancia: la última acaba centrada y, por decimales, se quedaba a una fracción del centro */
            if (passed === card.classList.contains('is-centered')) return;
            card.classList.toggle('is-centered', passed);
            hover(card, passed);
          });
        };
        horizontal.eventCallback('onUpdate', check);
        /* Al llegar al módulo, las que ya se ven se abren (y la del centro se
           pone cuadrada) aunque aún no se haya movido la fila */
        const section = grid.closest('.brand-team') || grid;
        ScrollTrigger.create({ trigger: section, start: enterAt('top 70%'), once: true, onEnter: check });
      } else {
        ScrollTrigger.batch(cards, {
          start: enterAt('top 85%'),
          once: true,
          onEnter: (batch) => batch.forEach((card, k) => open(card, k * 0.12)),
        });
      }
      /* Red de seguridad: una flor cerrada es una foto que no se ve */
      const watch = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        setTimeout(() => open(entry.target), 1500);
        watch.unobserve(entry.target);
      }), { threshold: 0.3 });
      if (!horizontal) cards.forEach((card) => watch.observe(card));
    })();

    /* title-section: la entrada de TODOS los bloques de título (Guille, 29/09)
       La misma que se ajustó en el hero, convertida en sistema. Cada bloque se
       declara con data-motion="title-section" y marca sus piezas:
         .eyebrow          antetítulo
         [data-title]      titular (y su marca de rotulador .mk, si la lleva)
         [data-title-sub]  subtítulo o entradilla (uno o varios párrafos)
         [data-title-cta]  el botón del bloque
       Orden de lectura, cada pieza cuando la anterior ya casi ha llegado:
       antetítulo → titular línea a línea desde su máscara → se traza la marca →
       subtítulo línea a línea → y el BOTÓN el último, fuera del stagger (regla 5
       del theme). Entra una vez, al asomar el bloque; lo que ya está en pantalla
       al cargar (el hero) entra solo, con un pequeño retraso.
       Al terminar se deshace el corte en líneas: la máscara recortaría las marcas
       que cuelgan por debajo de la línea.
       Sin motion o sin GSAP: todo visible desde el principio, con las marcas
       ya trazadas. */
    (() => {
      const show = () => document.documentElement.classList.remove('js-intro');
      const sections = [...document.querySelectorAll('[data-motion="title-section"]')];
      if (!window.SplitText || !sections.length) { show(); return; }

      /* Marcas de rotulador (Guille, 29/09: «noto que me atasco»). Dos cambios:
         1. Se dibujan SIN MÁSCARA: el titular entra por líneas con una máscara
            que recorta cada línea, y el subrayado cuelga por debajo; antes se
            trazaba medio tapado y, al quitar la máscara al final, aparecía entero
            de golpe. Ahora, en cuanto el titular llega, se le quita la máscara y
            solo entonces se dibuja la marca, entera a la vista.
         2. Se dibujan como un TRAZO (stroke-dashoffset a lo largo del propio
            camino, que trae pathLength="1"), no destapadas con un rectángulo:
            la punta avanza como la de un rotulador. */
      const drawMarks = (title) => {
        const svgs = [...title.querySelectorAll('.mk svg')];
        const paths = svgs.map((svg) => svg.querySelector('path')).filter(Boolean);
        if (!paths.length) return;
        /* ⚠️ La longitud se MIDE: con vector-effect: non-scaling-stroke (subrayado
           y tachado) el trazo se mide en píxeles de PANTALLA y pathLength deja de
           valer —el patrón se descuadraba: asomaba un trozo a la derecha y la marca
           se quedaba corta—. Sin esa propiedad (el círculo), en unidades del SVG.
           Mientras se dibuja se quita pathLength; al acabar se limpia todo y la
           marca queda como en el diseño. */
        const lengthOf = (path) => {
          if (getComputedStyle(path).vectorEffect !== 'non-scaling-stroke') return path.getTotalLength();
          const ctm = path.getScreenCTM();
          const total = path.getTotalLength();
          let len = 0, prev = null;
          for (let i = 0; i <= 60; i++) {
            const pt = path.getPointAtLength((total * i) / 60).matrixTransform(ctm);
            if (prev) len += Math.hypot(pt.x - prev.x, pt.y - prev.y);
            prev = pt;
          }
          return len;
        };
        paths.forEach((path) => {
          const pathLength = path.getAttribute('pathLength');
          path.removeAttribute('pathLength');
          const len = lengthOf(path);
          /* Hueco mayor que el trazo y arranque un poco más allá: con el trazo
             «vacío», el remate redondeado no deja un punto al principio */
          gsap.set(path, { attr: { 'stroke-dasharray': `${len} ${len + 20}`, 'stroke-dashoffset': len + 10 } });
          path.dataset.pathLength = pathLength || '';
        });
        gsap.set(svgs, { opacity: 1 });
        /* ⚠️ Por ATRIBUTO, no por estilo: GSAP redondea los px del estilo */
        gsap.to(paths, {
          attr: { 'stroke-dashoffset': 0 }, duration: 0.85, ease: 'power2.inOut', stagger: 0.1,
          onComplete: () => paths.forEach((path) => {
            path.removeAttribute('stroke-dasharray');
            path.removeAttribute('stroke-dashoffset');
            if (path.dataset.pathLength) path.setAttribute('pathLength', path.dataset.pathLength);
            delete path.dataset.pathLength;
          }),
        });
      };

      document.fonts.ready.then(() => {
        sections.forEach((section) => {
          const eyebrow = section.querySelector('.eyebrow');
          const title = section.querySelector('[data-title]');
          const subs = [...section.querySelectorAll('[data-title-sub]')];
          const cta = section.querySelector('[data-title-cta]');
          const parts = [eyebrow, title, ...subs, cta].filter(Boolean);

          const splits = [];
          const lines = (el) => {
            if (!el) return [];
            const split = SplitText.create(el, { type: 'lines', mask: 'lines' });
            splits.push(split);
            return split.lines;
          };
          const eyebrowLines = lines(eyebrow);
          const titleLines = lines(title);
          const titleSplit = title ? splits[splits.length - 1] : null;
          /* La marca, oculta mientras el titular sube dentro de su máscara */
          const marks = title ? [...title.querySelectorAll('.mk svg')] : [];
          const allSubLines = subs.flatMap(lines);

          gsap.set([...eyebrowLines, ...titleLines, ...allSubLines], { yPercent: 105 });
          if (marks.length) gsap.set(marks, { opacity: 0 });
          if (cta) gsap.set(cta, { y: 16, autoAlpha: 0 });
          /* Ya colocadas (y ocultas en su máscara): fuera el visibility del CSS */
          gsap.set(parts.filter((el) => el !== cta), { visibility: 'visible' });

          const tl = gsap.timeline({
            paused: true,
            onComplete: () => splits.forEach((s) => s.revert()),
          });
          if (eyebrowLines.length) tl.to(eyebrowLines, { yPercent: 0, duration: 0.8, ease: 'expo.out' });
          if (titleLines.length) {
            tl.to(titleLines, { yPercent: 0, duration: 1.1, ease: 'expo.out', stagger: 0.12 }, eyebrowLines.length ? '-=0.6' : 0);
            /* expo.out: a 0,3 s del final las líneas ya están en su sitio (a
               menos de medio píxel), así que quitar la máscara ahí no se nota */
            tl.call(() => {
              titleSplit.revert();
              splits.splice(splits.indexOf(titleSplit), 1);
              drawMarks(title);
            }, null, '-=0.3');
          }
          if (allSubLines.length) tl.to(allSubLines, { yPercent: 0, duration: 0.9, ease: 'expo.out', stagger: 0.08 }, '-=0.1');
          if (cta) tl.to(cta, { y: 0, autoAlpha: 1, duration: 0.7, ease: 'power3.out' }, '+=0.05');

          /* Lo que ya se ve al cargar, con un respiro; lo demás, al asomar.
             ⚠️ Se mide desde el PRIMER TEXTO del bloque, no desde el contenedor:
             en el cierre de Filosofía el contenedor empieza por el bol (230 px) y
             la frase se animaba todavía por debajo de la pantalla, sin que nadie
             la viera. */
          const lead = eyebrow || title || section;
          const onScreen = lead.getBoundingClientRect().top < innerHeight * (ENTER_LEAD ? (100 + ENTER_LEAD) / 100 : 0.85);
          if (onScreen) tl.delay(0.35).play();
          else ScrollTrigger.create({ trigger: lead, start: enterAt('top 85%'), once: true, onEnter: () => tl.play() });

          /* ⚠️ RED DE SEGURIDAD (como reveal-cta): un título o un botón invisible
             es contenido roto. Si el bloque lleva 1,5 s en pantalla y la entrada
             no ha arrancado (salto con ancla, recarga a media página), se
             completa de golpe. */
          const watch = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) return;
            setTimeout(() => { if (!tl.isActive() && tl.progress() === 0) tl.progress(1); }, 1500);
            watch.disconnect();
          }, { threshold: 0.3 });
          watch.observe(lead);
        });
      }).catch(show);
    })();

    /* logo-marquee: la cinta de logos de distribuidores (Guille, 30/09)
       Corre sola a la velocidad de la bandana (--motion-ribbon-speed) y, si
       se hace scroll con la cinta en pantalla, pasa más rápido y vuelve a su
       ritmo al parar (mismo empuje y frenada que la bandana). La fila se
       duplica una vez para el bucle; las copias van aria-hidden. Sin motion:
       la fila se desliza con el dedo y no se mueve sola (CSS). */
    (() => {
      const BASE = token('--motion-ribbon-speed', 40);
      const BOOST = 0.6;    /* cuánto empuja cada px/s de scroll */
      const DECAY = 0.92;   /* por fotograma: el empujón se apaga en ~0,5 s */
      document.querySelectorAll('[data-motion="logo-marquee"]').forEach((viewport) => {
        const track = viewport.querySelector('.logo-marquee__track');
        const originals = track ? [...track.children] : [];
        if (!originals.length) return;
        originals.forEach((item) => {
          const copy = item.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('data-shopify-editor-block');
          track.append(copy);
        });
        viewport.classList.add('is-running');
        viewport.removeAttribute('tabindex');
        /* Lo que mide una tanda: del primer original a su copia */
        const loop = () => track.children[originals.length].offsetLeft - originals[0].offsetLeft;
        let offset = 0;
        let push = 0;
        let lastY = window.scrollY;
        let visible = false;
        new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; }).observe(viewport);
        gsap.ticker.add((time, deltaTime) => {
          const dt = Math.min(deltaTime, 64) / 1000;
          if (!dt) return;
          const y = window.scrollY;
          const speed = Math.abs(y - lastY) / dt;
          lastY = y;
          if (!visible) return;
          /* Mismo filtro que la bandana: el empujón se suma poco a poco y se
             apaga solo. En valor absoluto: subir o bajar, siempre acelera */
          push = push * DECAY + speed * BOOST * (1 - DECAY);
          offset -= (BASE + push) * dt;
          const L = loop();
          if (L > 0 && offset <= -L) offset += L;
          track.style.transform = `translate3d(${offset.toFixed(2)}px, 0, 0)`;
        });
      });
    })();

    /* expand: la foto con margen y esquinas se amplía hasta llenar la pantalla
       (talent-card, Figma «The Club-CTA»)
       Del recorte de reposo (margen, esquinas y alto de 658) a sangre y sin
       redondeo cuando el escenario llega arriba; a la vez la foto se acerca un
       poco (1,12 → 1). Un solo progreso del que salen margen, alto, radio y
       zoom, para que vayan exactamente al mismo ritmo. Se animan tres
       variables del clip-path, no el clip-path entero (ver talent-card).
       Sin motion: la foto se queda en su recorte de reposo. */
    (() => {
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      /* ⚠️ Las medidas llegan como texto («80px», «5rem»): parseFloat a secas
         leería «5rem» como 5 px */
      const toPx = (v) => (v.trim().endsWith('rem') ? parseFloat(v) * rootPx : parseFloat(v));
      const ease = gsap.parseEase('power2.inOut');
      document.querySelectorAll('[data-motion="expand"]').forEach((stage) => {
        const host = stage.closest('.talent-card') || stage;
        const photo = stage.querySelector('img');
        const read = (name, fallback) => toPx(getComputedStyle(host).getPropertyValue(name)) || fallback;
        const apply = (p) => {
          const e = ease(p);
          const insetY = Math.max(0, (stage.offsetHeight - read('--tc-rest-h', 658)) / 2);
          stage.style.setProperty('--expand-y', `${(insetY * (1 - e)).toFixed(2)}px`);
          stage.style.setProperty('--expand-x', `${(read('--tc-margin', 80) * (1 - e)).toFixed(2)}px`);
          stage.style.setProperty('--expand-r', `${(read('--tc-radius', 48) * (1 - e)).toFixed(2)}px`);
          if (photo) photo.style.scale = (1.12 - 0.12 * e).toFixed(4);
        };
        const state = { p: 0 };
        /* ESCRITORIO: el módulo llega ENMARCADO, se queda fijo arriba y el
           scroll expande la foto; luego sigue (Guille, 30/09: «primero se ve
           en frame y luego la foto se expande»). Antes la expansión iba atada
           a la entrada del módulo, así que al tenerlo delante ya estaba casi
           a sangre, y con la guía de scroll se hacía entera durante el salto.
           Sus dos paradas (enmarcada / a sangre) las lee la guía de scroll.
           MÓVIL: como antes, ligada a la entrada. */
        const desktop = window.matchMedia('(min-width: 750px)').matches;
        const tween = gsap.to(state, {
          p: 1,
          ease: 'none',
          onUpdate: () => apply(state.p),
          scrollTrigger: desktop
            ? { trigger: host, start: 'top top', end: () => `+=${Math.round(window.innerHeight * 0.7)}`, pin: true, scrub: LAG, invalidateOnRefresh: true, onRefresh: (self) => apply(self.progress) }
            : { trigger: stage, start: 'top bottom', end: 'top top', scrub: LAG, invalidateOnRefresh: true, onRefresh: (self) => apply(self.progress) },
        });
        if (desktop) {
          const st = tween.scrollTrigger;
          host._expandStops = () => [st.start, st.end];
        }
        apply(0);
      });
    })();

    /* converge: módulos de contenido + imagen que entran separados y se juntan
       (Guille, 29/09)
       El módulo lleva data-motion="converge" y sus dos columnas data-converge.
       Al asomar, cada columna viene desplazada hacia SU lado (la de la izquierda,
       más a la izquierda; la de la derecha, más a la derecha) y, ligado al scroll,
       se juntan hasta encajar cuando el centro del módulo llega al centro de la
       pantalla; de ahí en adelante, quietas.
       El lado se MIDE (dónde cae cada columna), no se deduce del orden del DOM:
       así vale igual con el contenido a la izquierda o a la derecha (Historia 2).
       Es un desplazamiento en x: convive con el parallax vertical de data-depth
       (GSAP compone x e y en el mismo transform sin pisarse).
       Distancia: --motion-converge-distance (% del ancho de pantalla).
       Sin motion: cada columna en su sitio. */
    (() => {
      const DISTANCE = token('--motion-converge-distance', 10);
      /* En móvil las columnas van apiladas: no hay lados de los que juntarse */
      if (matchMedia('(max-width: 749px)').matches) return;
      document.querySelectorAll('[data-motion="converge"]').forEach((module) => {
        const cols = [...module.querySelectorAll('[data-converge]')];
        if (cols.length < 2) return;
        const center = (el) => { const r = el.getBoundingClientRect(); return r.left + r.width / 2; };
        const mid = cols.reduce((sum, el) => sum + center(el), 0) / cols.length;
        cols.forEach((col) => {
          const side = center(col) < mid ? -1 : 1;
          gsap.fromTo(col, { x: () => side * innerWidth * (DISTANCE / 100) }, {
            x: 0,
            ease: 'power2.out',
            scrollTrigger: { trigger: module, start: 'top bottom', end: 'center center', scrub: LAG, invalidateOnRefresh: true },
          });
        });
      });
    })();

    /* depth: capas de profundidad ligadas al scroll
       Cada [data-depth] NACE desplazado y llega a su sitio (desplazamiento 0)
       justo cuando su centro coincide con el centro de la pantalla; al seguir
       bajando, continúa en el mismo sentido. El recorrido es proporcional a su
       «profundidad»: las fotos (0,16) viajan más que los textos (0,05), así que
       la página se lee por capas. Lineal y simétrico → el cero cae en el centro.
       Las tarjetas del equipo alternan velocidad por columna,
       para que la rejilla no se mueva como un bloque.
       Va con inercia (scrub) para que el movimiento sea fluido, no pegado a la
       rueda. Sin motion: nada se mueve. */
    (() => {
      const LAG = token('--motion-depth-lag', 0.9);
      document.querySelectorAll('[data-team-morph] .brand-team__grid:not(.is-scroll-carousel) .team-member').forEach((card, i) => {
        card.dataset.depth = [0.05, 0.11, 0.07, 0.13][i % 4];
      });
      /* ⚠️ Las pegatinas de Moods NO: el cordón se dibuja midiendo dónde están,
         y si flotan con el scroll el cordón deja de caer sobre ellas. */
      /* Dentro de un módulo fijado (el carrusel del equipo en móvil) la
         profundidad vertical no tiene sentido: el módulo no se mueve */
      const pinnedCarousel = document.querySelector('.brand-team__grid.is-scroll-carousel')?.closest('.brand-team');
      document.querySelectorAll('[data-depth]').forEach((el) => {
        if (pinnedCarousel?.contains(el)) return;
        const speed = parseFloat(el.dataset.depth) || 0;
        if (!speed) return;
        const travel = () => ((innerHeight + el.offsetHeight) / 2) * speed;
        gsap.fromTo(el, { y: () => travel() }, {
          y: () => -travel(),
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: LAG, invalidateOnRefresh: true },
        });
      });
    })();

    /* logo-assemble: el logo del footer se monta letra a letra (Guille, 29/09)
       El SVG va en línea con cada pieza en su <g data-lg="...">:
         building  el Empire State: sube desde la base, como si se levantara
         window    sus ventanas: se encienden una a una
         letter    «LA NEWYORKINA»: llegan desordenadas —cada una desde su sitio,
                   girada y pequeña— y encajan con un rebote, en orden de lectura
         reg       el ®: aparece girando al final
         claim     «GRANOLA CON MUCHO WITH»: cae letra a letra, más rápido
       Se anima el <g> y nunca el <path>: el path trae su propio transform (el
       del export) y GSAP lo pisaría.
       El desorden es FIJO (pseudoaleatorio por índice), no Math.random: el logo
       se monta igual en cada visita y en cada captura.
       TAMBIÉN EN TEXTO (Guille, 29/09: el correo del footer): si el elemento no
       es un SVG, se parte en letras con SplitText y cada letra entra igual que
       las del logo; el desorden se mide en su tamaño de letra, no en unidades del
       SVG. Al terminar se deshace el corte.
       Una sola vez, al asomar. Sin motion: quieto y entero. */
    (() => {
      document.querySelectorAll('[data-motion="logo-assemble"]').forEach((logo) => {
        const isText = !(logo instanceof SVGElement);
        const split = isText && window.SplitText ? SplitText.create(logo, { type: 'chars' }) : null;
        if (isText && !split) return;
        const pick = (name) => [...logo.querySelectorAll(`[data-lg="${name}"]`)];
        const building = pick('building');
        const windows = pick('window');
        const letters = split ? split.chars : pick('letter');
        const reg = pick('reg');
        const claim = pick('claim');
        /* Cuánto se desordenan: unidades del viewBox en el SVG; en texto, en
           proporción a su cuerpo de letra (el mismo desorden, a su escala). */
        const unit = split ? parseFloat(getComputedStyle(logo).fontSize) / 30 : 1;

        /* Pseudoaleatorio estable en [-1, 1] */
        const rand = (i, salt) => {
          const x = Math.sin((i + 1) * 12.9898 + salt * 78.233) * 43758.5453;
          return (x - Math.floor(x)) * 2 - 1;
        };
        const origin = { transformOrigin: '50% 50%' };

        gsap.set(building, { ...origin, yPercent: 40, autoAlpha: 0 });
        gsap.set(windows, { ...origin, scale: 0, autoAlpha: 0 });
        gsap.set(letters, {
          ...origin,
          x: (i) => rand(i, 1) * 60 * unit,
          y: (i) => (-20 - Math.abs(rand(i, 2)) * 35) * unit,
          rotation: (i) => rand(i, 3) * 40,
          scale: 0.5,
          autoAlpha: 0,
        });
        gsap.set(reg, { ...origin, scale: 0, rotation: -180, autoAlpha: 0 });
        gsap.set(claim, { ...origin, y: -18, autoAlpha: 0 });

        const tl = gsap.timeline({ paused: true, onComplete: () => split?.revert() });
        if (building.length) tl.to(building, { yPercent: 0, autoAlpha: 1, duration: 0.9, ease: 'expo.out' });
        if (windows.length) tl.to(windows, { scale: 1, autoAlpha: 1, duration: 0.35, ease: 'back.out(2)', stagger: 0.06 }, '-=0.4');
        tl.to(letters, {
          x: 0, y: 0, rotation: 0, scale: 1, autoAlpha: 1,
          /* el correo tiene más letras que el logo: cascada más apretada */
          duration: 0.9, ease: 'back.out(1.6)', stagger: split ? 0.04 : 0.07,
        }, building.length ? 0.25 : 0);
        if (reg.length) tl.to(reg, { scale: 1, rotation: 0, autoAlpha: 1, duration: 0.6, ease: 'back.out(2.2)' }, '-=0.3');
        if (claim.length) tl.to(claim, { y: 0, autoAlpha: 1, duration: 0.45, ease: 'power3.out', stagger: 0.035 }, '-=0.5');

        /* En una cadena (data-motion-chain) no arranca sola: deja su timeline
           y es js/motion-chain.js quien la lanza en su turno. */
        if (logo.closest('[data-motion-chain]')) { logo.lnyTimeline = tl; return; }
        ScrollTrigger.create({ trigger: logo, start: enterAt('top 85%'), once: true, onEnter: () => tl.play() });
        /* Red de seguridad: un logo a medio montar es un logo roto */
        const watch = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          setTimeout(() => { if (tl.progress() === 0) tl.progress(1); }, 1500);
          watch.disconnect();
        }, { threshold: 0.3 });
        watch.observe(logo);
      });
    })();

    /* feature-cards: las tarjetas con ilustración entran progresivas, como las
       columnas del footer (Guille, 30/09): cada tarjeta arranca un poco
       después de la anterior y, dentro, primero la ilustración (sube con un
       pequeño rebote), luego el título y luego el texto. Una vez, al asomar.
       Red de seguridad: nada se queda invisible. Sin motion: quietas. */
    (() => {
      document.querySelectorAll('[data-motion="feature-cards"]').forEach((list) => {
        const cards = [...list.children];
        /* Las tres piezas por clase de feature-cards o por atributo
           (data-card-art/-title/-text): así otro módulo con la misma
           estructura (club-points) usa la entrada sin heredar su CSS */
        const parts = (card) => [card.querySelector('.feature-cards__art, [data-card-art]'), card.querySelector('.feature-cards__title, [data-card-title]'), card.querySelector('.feature-cards__text, [data-card-text]')];
        const all = cards.flatMap(parts).filter(Boolean);
        if (!all.length) return;
        gsap.set(all, { y: 24, autoAlpha: 0 });
        const tl = gsap.timeline({ paused: true });
        cards.forEach((card, i) => {
          const [art, title, text] = parts(card);
          const at = i * 0.15;
          if (art) tl.fromTo(art, { y: 24, scale: 0.9, autoAlpha: 0 }, { y: 0, scale: 1, autoAlpha: 1, duration: 0.7, ease: 'back.out(1.7)' }, at);
          if (title) tl.to(title, { y: 0, autoAlpha: 1, duration: 0.6, ease: 'expo.out' }, at + 0.15);
          if (text) tl.to(text, { y: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out' }, at + 0.25);
        });
        ScrollTrigger.create({ trigger: list, start: enterAt('top 85%'), once: true, onEnter: () => tl.play() });
        const watch = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          setTimeout(() => { if (tl.progress() === 0) tl.progress(1); }, 1500);
          watch.disconnect();
        }, { threshold: 0.2 });
        watch.observe(list);
      });
    })();

    /* footer-cols: las columnas del footer entran progresivas (Guille, 29/09)
       Cada columna arranca un poco después de la anterior (de izquierda a
       derecha) y, dentro de ella, primero el título y luego sus enlaces uno a
       uno, subiendo con un fundido. Una vez, al asomar las columnas.
       Son enlaces, no controles de formulario: pueden entrar animados, pero con
       red de seguridad para que ninguno se quede invisible. Sin motion: quietas. */
    (() => {
      document.querySelectorAll('[data-motion="footer-cols"]').forEach((wrap) => {
        const cols = [...wrap.children];
        const titleOf = (col) => col.querySelector('h2, h3, [class*="col-title"]');
        const all = cols.flatMap((col) => [titleOf(col), ...col.querySelectorAll('li')]).filter(Boolean);
        gsap.set(all, { y: 20, autoAlpha: 0 });

        const tl = gsap.timeline({ paused: true });
        cols.forEach((col, i) => {
          const title = titleOf(col);
          const items = [...col.querySelectorAll('li')];
          const at = i * 0.15;
          if (title) tl.to(title, { y: 0, autoAlpha: 1, duration: 0.6, ease: 'expo.out' }, at);
          if (items.length) tl.to(items, { y: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out', stagger: 0.06 }, at + 0.15);
        });

        /* En una cadena (data-motion-chain) no arranca sola: la lanza
           motion-chain en su turno. */
        if (wrap.closest('[data-motion-chain]')) { wrap.lnyTimeline = tl; return; }
        ScrollTrigger.create({ trigger: wrap, start: enterAt('top 85%'), once: true, onEnter: () => tl.play() });
        /* Red de seguridad: un enlace invisible es un enlace roto */
        const watch = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          setTimeout(() => { if (tl.progress() === 0) tl.progress(1); }, 1500);
          watch.disconnect();
        }, { threshold: 0.2 });
        watch.observe(wrap);
      });
    })();

    /* motion-chain: encadena animaciones que, sueltas, arrancarían a la vez
       (Guille, 29/09: en el footer, logo → columnas → correo).
       El contenedor lleva data-motion-chain y cada pieza data-chain-step="n".
       Las piezas NO arrancan solas: al ver la cadena, dejan su timeline en
       el.lnyTimeline (logo-assemble, footer-cols) y aquí se unen en orden en
       una timeline maestra, cada paso solapado un poco con el anterior para que
       la secuencia fluya. Arranca una vez, al asomar el primer paso.
       ⚠️ Va DESPUÉS de los handlers de sus piezas en el orden de los <script>.
       Sin motion: nada que encadenar (las piezas ya se quedan quietas). */
    (() => {
      const OVERLAP = 0.5;   /* s que un paso pisa el final del anterior */
      document.querySelectorAll('[data-motion-chain]').forEach((chain) => {
        const steps = [...chain.querySelectorAll('[data-chain-step]')]
          .sort((a, b) => Number(a.dataset.chainStep) - Number(b.dataset.chainStep))
          .filter((el) => el.lnyTimeline);
        if (!steps.length) return;

        const master = gsap.timeline({ paused: true });
        steps.forEach((el, i) => master.add(el.lnyTimeline.paused(false), i ? `-=${OVERLAP}` : 0));
        /* En móvil la cadena va al doble: el pie ocupa dos pantallas y, al
           ritmo de escritorio, se llegaba a las columnas antes de que hubieran
           entrado (un pantallazo granate vacío). Mismo orden, menos espera. */
        if (ENTER_LEAD) master.timeScale(2);

        ScrollTrigger.create({ trigger: steps[0], start: enterAt('top 85%'), once: true, onEnter: () => master.play() });
        /* Red de seguridad: si el footer lleva 1,5 s en pantalla y la cadena no
           ha arrancado, se completa entera (nada puede quedar invisible). */
        const watch = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          setTimeout(() => { if (master.progress() === 0 && !master.isActive()) master.progress(1); }, 1500);
          watch.disconnect();
        }, { threshold: 0.2 });
        watch.observe(steps[0]);
      });
    })();

    /* Ultimo paso, y es imprescindible: ScrollTrigger procesa los triggers en
       el ORDEN EN QUE SE CREARON, no en el que aparecen en la pagina. Si un
       modulo con pin (que anade altura al documento) se declara despues de
       otro que va mas abajo, el de abajo se queda con coordenadas viejas y
       sus animaciones no llegan a dispararse nunca.

       sort() los reordena por posicion real antes de recalcular. Con
       refresh() a secas no basta: hay que ordenarlos primero. */
    const resyncTriggers = () => {
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    };
    requestAnimationFrame(resyncTriggers);
    window.addEventListener('load', resyncTriggers);
    document.fonts.ready.then(resyncTriggers);

  });
})();

/**
 * LNYSliderDots — el indicador líquido de snippets/slider-dots (29/09).
 * Va FUERA del bloque de GSAP de arriba a propósito: los puntos tienen que
 * funcionar aunque GSAP no cargue (entonces la pastilla salta sin animar).
 *
 * Movimiento: 1) la gota se ESTIRA desde su sitio hasta cubrir el punto nuevo
 * (el borde de delante tira, el de detrás se queda; power2.in); 2) el borde de
 * detrás la alcanza y se RECOGE en el punto nuevo con un rebote pequeño
 * (elastic.out). Dura lo mismo que la transición del slider.
 */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const rootStyles = getComputedStyle(document.documentElement);
  const DURATION = (parseFloat(rootStyles.getPropertyValue('--motion-slider-duration')) || 1100) / 1000;

  const attach = (box) => {
    if (box.sliderDots) return box.sliderDots;
    const dots = [...box.querySelectorAll('.slider-dots__dot')];
    const pill = box.querySelector('.slider-dots__pill');
    if (!dots.length || !pill) return null;
    let current = Math.max(0, dots.findIndex((d) => d.getAttribute('aria-current') === 'true'));
    /* Ancho en reposo de la pastilla, medido una vez antes de animar nada */
    const W = pill.offsetWidth;
    const xOf = (k) => dots[k].offsetLeft + dots[k].offsetWidth / 2 - W / 2;
    const place = (k) => {
      pill.style.translate = 'none';
      if (window.gsap) window.gsap.set(pill, { x: xOf(k), width: W });
      else pill.style.transform = `translateX(${xOf(k)}px)`;
    };
    const mark = (k) => dots.forEach((d, i) => {
      if (i === k) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });

    const go = (to) => {
      if (to === current || to < 0 || to >= dots.length) return;
      const from = current;
      current = to;
      mark(to);
      if (reduce || !window.gsap) { place(to); return; }
      const w = W;
      const a = xOf(from), b = xOf(to);
      window.gsap.killTweensOf(pill);
      window.gsap.timeline()
        .to(pill, { x: Math.min(a, b), width: Math.abs(b - a) + w, duration: DURATION * 0.45, ease: 'power2.in' })
        .to(pill, { x: b, width: w, duration: DURATION * 0.55, ease: 'elastic.out(1, 0.75)' });
    };

    dots.forEach((dot, i) => dot.addEventListener('click', () => {
      box.dispatchEvent(new CustomEvent('slider-dots:select', { bubbles: true, detail: { index: i } }));
    }));
    requestAnimationFrame(() => place(current));
    box.sliderDots = { go, get index() { return current; } };
    return box.sliderDots;
  };

  const init = (scope = document) => scope.querySelectorAll('[data-slider-dots]').forEach(attach);
  window.LNYSliderDots = { attach, init };
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();

/* ⭐ ACORDEON (snippet product-accordion): apertura y cierre suaves, el
   mismo gesto que la FAQ (Guille, 02/10: «la forma de desplegar sutil y
   animada, la + que se pasa a X»). Se intercepta el clic del <summary>
   (tambien llega con Intro y Espacio) y se anima el alto del cuerpo; al
   cerrar, .is-closing devuelve el aspa a «+» y apaga el contenido antes de
   que el <details> se cierre de verdad. El estado lo sigue llevando el
   <details>. Con motion reducido, nativo. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const setup = (root) => root.querySelectorAll('details.accordion:not([data-accordion-ready])').forEach((item) => {
    item.dataset.accordionReady = '';
    const summary = item.querySelector('summary');
    const body = item.querySelector('.accordion__body');
    if (!summary || !body || !body.animate) return;
    let running = null;
    const finish = () => { if (running) running.cancel(); running = null; body.style.height = ''; };
    summary.addEventListener('click', (event) => {
      if (reduce.matches) return;
      event.preventDefault();
      finish();
      if (!item.open) {
        item.open = true;
        item.classList.remove('is-closing');
        running = body.animate({ height: ['0px', body.scrollHeight + 'px'] }, { duration: 480, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' });
        running.onfinish = finish;
      } else {
        item.classList.add('is-closing');
        running = body.animate({ height: [body.offsetHeight + 'px', '0px'] }, { duration: 360, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
        running.onfinish = () => { item.open = false; item.classList.remove('is-closing'); finish(); };
      }
    });
  });
  setup(document);
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
})();

